import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Mic, Square, Loader2, Save, Edit2, Trash2, X, Globe, Users as UsersIcon, ClipboardCopy as ClipboardCopyIcon, Plus, Download, FileText } from 'lucide-react';
import AudioWaveform from './AudioWaveform';
import CopyAllButton from './CopyAllButton';
import LanguageSelector from './LanguageSelector';
import TemplateSelector from './TemplateSelector';
import NoteStyleSelector from './NoteStyleSelector';
import SectionStyleSelector from './SectionStyleSelector';
import SendWithSelector from './SendWithSelector';
import MultiPatientConfirmDialog from './MultiPatientConfirmDialog';
import UserPromptSelector from './UserPromptSelector';
import PronounSelector from './PronounSelector';

// Utility function to render markdown text with bold and bullet points
const renderMarkdown = (text) => {
  if (!text) return null;

  // Split text into lines
  const lines = text.split('\n');
  const elements = [];
  let currentParagraph = [];
  let listItems = [];
  let key = 0;

  const flushParagraph = () => {
    if (currentParagraph.length > 0) {
      const paragraphText = currentParagraph.join(' ');
      elements.push(
        <p key={`p-${key++}`} className="mb-3 leading-relaxed">
          {parseInlineMarkdown(paragraphText)}
        </p>
      );
      currentParagraph = [];
    }
  };

  const flushList = () => {
    if (listItems.length > 0) {
      elements.push(
        <ul key={`ul-${key++}`} className="mb-3 ml-4 space-y-2">
          {listItems.map((item, idx) => (
            <li key={`li-${idx}`} className="flex">
              <span className="mr-2">•</span>
              <span className="flex-1">{parseInlineMarkdown(item)}</span>
            </li>
          ))}
        </ul>
      );
      listItems = [];
    }
  };

  const parseInlineMarkdown = (line) => {
    if (!line) return null;

    // Parse bold text (**text**)
    const parts = [];
    let lastIndex = 0;
    const boldRegex = /\*\*(.*?)\*\*/g;
    let match;

    while ((match = boldRegex.exec(line)) !== null) {
      // Add text before the bold
      if (match.index > lastIndex) {
        parts.push(line.substring(lastIndex, match.index));
      }
      // Add bold text
      parts.push(<strong key={`bold-${match.index}`} className="font-semibold">{match[1]}</strong>);
      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < line.length) {
      parts.push(line.substring(lastIndex));
    }

    return parts.length > 0 ? parts : line;
  };

  lines.forEach((line, index) => {
    const trimmedLine = line.trim();

    // Empty line - separator between paragraphs/lists
    if (trimmedLine === '') {
      flushList();
      flushParagraph();
      return;
    }

    // Bullet point (starts with - or * followed by space)
    if (/^[-*]\s+/.test(trimmedLine)) {
      flushParagraph();
      const bulletText = trimmedLine.replace(/^[-*]\s+/, '');
      listItems.push(bulletText);
      return;
    }

    // Regular text - add to current paragraph
    flushList();
    currentParagraph.push(trimmedLine);
  });

  // Flush any remaining content
  flushList();
  flushParagraph();

  return elements;
};

// HPI headings for paragraph formatting
const HPI_HEADINGS = [
  'CC:', 'Chief Complaint:', 'Chief complaint:',
  'Associated Symptoms:', 'Associated symptoms:',
  'Factors & Treatment:', 'Factors & treatment:', 'Factors and Treatment:', 'Factors and treatment:',
  'Additional Info:', 'Additional info:', 'Additional Information:', 'Additional information:',
  'Context:', 'CONTEXT:',
  'Location:', 'LOCATION:',
  'Quality:', 'QUALITY:',
  'Severity:', 'SEVERITY:',
  'Duration:', 'DURATION:',
  'Timing:', 'TIMING:',
  'Modifiers:', 'MODIFIERS:', 'Modifying Factors:', 'Modifying factors:',
  'Associated w/:', 'Associated with:', 'Associated W/:',
  'Onset:', 'ONSET:',
  'Character:', 'CHARACTER:',
  'Aggravating:', 'AGGRAVATING:', 'Aggravating Factors:', 'Aggravating factors:',
  'Relieving:', 'RELIEVING:', 'Relieving Factors:', 'Relieving factors:',
  'Treatment:', 'TREATMENT:', 'Treatments:', 'TREATMENTS:',
  'Setting:', 'SETTING:'
];

// Physical Exam headings for paragraph formatting
const PE_HEADINGS = [
  'GENERAL APPEARANCE:', 'General Appearance:', 'General appearance:',
  'General:', 'GENERAL:', 'Gen:',
  'Constitutional:', 'CONSTITUTIONAL:', 'Vitals:', 'VITALS:',
  'HEENT:', 'Heent:',
  'HEAD:', 'Head:',
  'EYES:', 'Eyes:',
  'EARS:', 'Ears:',
  'NOSE:', 'Nose:',
  'ORAL CAVITY:', 'Oral Cavity:', 'Oral cavity:', 'Mouth:', 'MOUTH:',
  'THROAT:', 'Throat:',
  'NECK/THYROID:', 'Neck/Thyroid:', 'NECK:', 'Neck:',
  'LYMPH NODES:', 'Lymph Nodes:', 'Lymph nodes:', 'Lymphatic:', 'LYMPHATIC:', 'Lymph:',
  'Cardiovascular:', 'CARDIOVASCULAR:', 'HEART:', 'Heart:', 'CV:', 'Cardiac:',
  'Respiratory:', 'RESPIRATORY:', 'LUNGS:', 'Lungs:', 'Pulmonary:', 'PULMONARY:', 'CHEST:', 'Chest:',
  'BREASTS:', 'Breasts:', 'Breast:', 'BREAST:',
  'ABDOMEN:', 'Abdomen:', 'Abdominal:', 'ABDOMINAL:', 'GI:', 'Abd:',
  'RECTAL:', 'Rectal:',
  'BACK:', 'Back:', 'Spine:', 'SPINE:',
  'MUSCULOSKELETAL:', 'Musculoskeletal:', 'MSK:',
  'EXTREMITIES:', 'Extremities:', 'Ext:',
  'NEUROLOGIC:', 'Neurologic:', 'NEUROLOGICAL:', 'Neurological:', 'Neuro:', 'NEURO:',
  'PSYCH:', 'Psych:', 'PSYCHIATRIC:', 'Psychiatric:', 'Mental Status:', 'MENTAL STATUS:',
  'SKIN:', 'Skin:', 'Dermatologic:', 'Derm:', 'DERMATOLOGIC:',
  'GU:', 'Genitourinary:', 'GENITOURINARY:',
  'Context:', 'Location:', 'Quality:', 'Severity:', 'Duration:', 'Timing:', 'Modifiers:', 'Associated w/:'
];

const formatRecordingDuration = (totalSeconds) => {
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) {
    return '0s';
  }

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const parts = [];

  if (hours > 0) {
    parts.push(`${hours}h`);
  }
  if (minutes > 0 || hours > 0) {
    parts.push(`${minutes}m`);
  }
  parts.push(`${seconds}s`);

  return parts.join(' ');
};

const getCompletedRecordingDuration = (startedAt) => {
  if (!startedAt) {
    return 0;
  }

  const elapsedMs = Date.now() - startedAt;
  if (elapsedMs <= 0) {
    return 0;
  }

  return Math.max(1, Math.round(elapsedMs / 1000));
};

// Helper function to format text with inline headings (adds line breaks before headings)
const formatWithInlineHeadings = (inputText, headings) => {
  if (!inputText) return inputText;
  let processedText = inputText.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

  // Sort headings by length (longest first) to avoid partial matches
  const sortedHeadings = [...headings].sort((a, b) => b.length - a.length);

  // Insert newlines before each heading (except if at start or already has newline)
  for (const heading of sortedHeadings) {
    const regex = new RegExp(`(?<!^)(?<!\n)\\s*(${heading.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'g');
    processedText = processedText.replace(regex, '\n$1');
  }

  return processedText;
};

const CopyButton = ({ textToCopy, ariaLabel }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    // Remove markdown formatting for copying
    const cleanText = textToCopy.replace(/\*\*(.*?)\*\*/g, '$1');
    navigator.clipboard.writeText(cleanText)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch((err) => {
        console.error('Failed to copy: ', err);
      });
  };

  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        handleCopy();
      }}
      className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 flex items-center"
      aria-label={ariaLabel}
    >
      {copied ? (
        <span className="flex items-center">
          <svg
            className="h-5 w-5 text-green-500 mr-1"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          Copied
        </span>
      ) : (
        <span className="flex items-center">
          Copy
        </span>
      )}
    </button>
  );
};

const sectionOptions = [
  { label: 'History of Present Illness', key: 'hpi' },
  { label: 'Review of Symptoms', key: 'ros' },
  { label: 'Assessment & Plan', key: 'assessment_plan' },
  { label: 'Additional MDM', key: 'additional_mdm' },
  { label: 'Patient Instructions', key: 'after_visit_summary' },
  { label: 'Physical Exam', key: 'physical_exam' },
  { label: 'Additional MDM', key: 'additional_mdm'},
];

const NOTE_SYNC_FIELDS = [
  'visit_summary',
  'hpi',
  'assessment_plan',
  'ros',
  'subjective',
  'objective',
  'assessment',
  'plan',
  'suggested_tests',
  'icd_codes',
  'em_codes',
  'cpt_codes',
  'physical_exam',
  'after_visit_summary',
  'em_education',
  'additional_mdm',
  'mdm_calculator',
  'ma_medications',
  'ma_inhouse_labs',
  'ma_sendout_labs',
  'ma_procedures',
  'format',
  'soap_created_at'
];

const buildEditedDataFromPatient = (patient, customSections = []) => {
  const hasSoapNote = Boolean(patient?.has_soap_note);
  const baseData = hasSoapNote ? {
    visit_summary: patient?.visit_summary || '',
    hpi: patient?.hpi || '',
    assessment_plan: patient?.assessment_plan || '',
    ros: patient?.ros || '',
    subjective: patient?.subjective || '',
    objective: patient?.objective || '',
    assessment: patient?.assessment || '',
    plan: patient?.plan || '',
    suggested_tests: patient?.suggested_tests || [],
    icd_codes: patient?.icd_codes || [],
    em_codes: patient?.em_codes || [],
    cpt_codes: patient?.cpt_codes || [],
    physical_exam: patient?.physical_exam || [],
    after_visit_summary: patient?.after_visit_summary || [],
    em_education: patient?.em_education || [],
    additional_mdm: patient?.additional_mdm || [],
    mdm_calculator: patient?.mdm_calculator || [],
    ma_medications: patient?.ma_medications || [],
    ma_inhouse_labs: patient?.ma_inhouse_labs || [],
    ma_sendout_labs: patient?.ma_sendout_labs || [],
    ma_procedures: patient?.ma_procedures || [],
  } : {
    visit_summary: [],
    hpi: '',
    assessment_plan: '',
    ros: '',
    subjective: '',
    objective: '',
    assessment: '',
    plan: '',
    suggested_tests: [],
    icd_codes: [],
    em_codes: [],
    cpt_codes: [],
    physical_exam: [],
    after_visit_summary: [],
    em_education: [],
    additional_mdm: [],
    mdm_calculator: [],
    ma_medications: patient?.ma_medications || [],
    ma_inhouse_labs: patient?.ma_inhouse_labs || [],
    ma_sendout_labs: patient?.ma_sendout_labs || [],
    ma_procedures: patient?.ma_procedures || [],
  };

  customSections.forEach(section => {
    const customKey = `custom_${section.id}`;
    baseData[customKey] = patient?.[customKey] || [''];
  });

  return baseData;
};

const sectionStyleMap = {
  'hpi': 'History of Present Illness',
  'ros': 'Review of Symptoms',
  'assessment_plan': 'Assessment & Plan',
  'additional_mdm': 'Additional MDM',
  'mdm_calculator': 'MDM Calculator',
  'after_visit_summary': 'Patient Instructions',
  'physical_exam': 'Physical Exam',
};

function sectionKeyForTitle(title) {
  switch (title) {
    case 'History of Present Illness': return 'hpi';
    case 'Review of Symptoms': return 'ros';
    case 'Assessment & Plan': return 'assessment_plan';
    case 'Additional MDM': return 'additional_mdm';
    case 'MDM Calculator': return 'mdm_calculator';
    case 'Patient Instructions': return 'after_visit_summary';
    case 'Physical Exam': return 'physical_exam';
    default: return '';
  }
}

// Add constants before the EditableSection component
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'image/jpeg',
  'image/png'
];

// Helper function to format visit type display names
const getVisitTypeDisplayName = (visitType) => {
  if (!visitType) return null;

  const displayNames = {
    'pre-employment': 'Pre-Employment Physicals',
    'follow-up': 'Workers Comp & First Aid (Follow Up)',
    'first-visit': 'Workers Comp & First Aid (First Visit)',
    'urgent-care': 'Urgent Care'
  };

  return displayNames[visitType] || visitType;
};

// Helper function to format patient name based on location
const formatPatientName = (patient) => {
  if (!patient) return '';

  // For miracletouch location, show as "Last Name, First Name"
  if (patient.location === 'miracletouch') {
    const lastName = patient.last_name || '';
    const firstName = patient.first_name || '';
    const middleInitial = patient.middle_initial ? ` ${patient.middle_initial}.` : '';

    if (lastName && firstName) {
      return `${lastName}, ${firstName}${middleInitial}`;
    }
  }

  // For all other locations, use the standard name field
  return patient.name || '';
};

// 3. Fix the validateFile function (it was referencing non-existent setFileError):
const validateFile = (file) => {
  if (!file) return { valid: false, error: 'No file selected' };
  
  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    return { valid: false, error: 'File size must be less than 10MB' };
  }
  
  // Check file type
  if (!ALLOWED_FILE_TYPES.includes(file.type)) {
    return { valid: false, error: 'File type not supported. Please upload a PDF, Word document, text file, or image.' };
  }
  
  return { valid: true, error: null };
};

// Upload Modal Component
const UploadModal = ({ isOpen, onClose, onUpload, isUploading, uploadError, uploadedFile, onRemoveFile }) => {
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const formatFileSize = (bytes = 0) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const handleUpload = async () => {
    if (file) {
      await onUpload(file);
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-800">
            {uploadedFile ? 'Reference Document' : 'Upload Reference Document'}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Show existing file if present */}
          {uploadedFile && (
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Current Document:</p>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md border border-gray-200">
                <div className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                          d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-gray-900 truncate max-w-xs">
                      {uploadedFile.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {uploadedFile.size ? formatFileSize(uploadedFile.size) : 'Unknown size'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <a
                    href={uploadedFile.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="text-teal-600 hover:text-teal-700"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </a>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveFile();
                    }}
                    className="text-red-600 hover:text-red-700"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Upload new file section */}
          {!uploadedFile && (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                className="hidden"
                accept={ALLOWED_FILE_TYPES.join(',')}
              />
              <div className="space-y-4">
                <div className="flex flex-col items-center">
                  <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p className="mt-2 text-sm text-gray-600">
                    Drag and drop your file here, or{' '}
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="text-teal-600 hover:text-teal-700 font-medium"
                    >
                      browse
                    </button>
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    Supported formats: PDF, Word, Text, Images (max 10MB)
                  </p>
                </div>

                {file && (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-gray-700">Selected file:</p>
                    <div className="mt-2 flex items-center justify-between p-3 bg-gray-50 rounded-md border border-gray-200">
                      <div className="flex items-center space-x-3">
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        <div>
                          <p className="text-sm font-medium text-gray-900 truncate max-w-xs">
                            {file.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {formatFileSize(file.size)}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => setFile(null)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}

                {uploadError && (
                  <div className="mt-4 text-sm text-red-600">
                    {uploadError}
                  </div>
                )}

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpload}
                    disabled={!file || isUploading}
                    className={`px-4 py-2 text-sm font-medium text-white rounded-md ${
                      !file || isUploading
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-teal-600 hover:bg-teal-700'
                    }`}
                  >
                    {isUploading ? (
                      <span className="flex items-center">
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Uploading...
                      </span>
                    ) : (
                      'Upload'
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const EditableSection = ({
  title,
  items,
  onUpdate,
  activeSection,
  onSectionClick,
  isCompact = false,
  userSettings = {},
  isSoapFormat = false,
  sectionStyle = 'normal',
  onSectionStyleChange,
  customSectionId = null,
  onDeleteSection = null,
  sendWithValue = 'none',
  onSendWithChange = null,
  enableSendWithSelector = false,
  maView = false
}) => {
  //const [isEditing, setIsEditing] = useState(false);
  const isEditing = activeSection === title;
  const [editedItems, setEditedItems] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showExamples, setShowExamples] = useState(false);
  const [learnedExamples, setLearnedExamples] = useState([]);
  const [isLearning, setIsLearning] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const examplesRef = useRef(null);
  const textareaRef = useRef(null);
  const buttonRef = useRef(null);
  const dropdownMenuRef = useRef(null);
  const initialRenderRef = useRef(true);
  const [isDirty, setIsDirty] = useState(false);
  const debounceTimeoutRef = useRef(null);
  const isVisitSummary = title === "Visit Summary";
  const contentHeight = isCompact ? "max-h-64" : "max-h-96"; // Height limits for different sections
  const [localText, setLocalText] = useState('');
  const [isLearned, setIsLearned] = useState(false);
  const [useFormatted, setUseFormatted] = useState(true);
  const [selectedExampleSection, setSelectedExampleSection] = useState(sectionKeyForTitle(title));
  const [currentExampleIdx, setCurrentExampleIdx] = useState(0);
  const [editedPatientInfo, setEditedPatientInfo] = useState({
    first_name: '',
    last_name: '',
    middle_initial: '',
    dobMonth: '',
    dobDate: '',
    dobYear: '',
    legal_sex: '',
    email: '',
    phone: '',
    pronouns: '',
  });
  const [ehrErrorStatus, setEhrErrorStatus] = useState(null);
  const [patientSendingEHR, setPatientSendingEHR] = useState(null);
  const sectionKey = customSectionId ? `custom_${customSectionId}` : sectionKeyForTitle(title);
  const shouldShowSendWithSelector = onSendWithChange && (customSectionId || enableSendWithSelector) && sectionKey;
  
  // Define section options for the learned examples modal
  const sectionOptions = [
    { key: 'hpi', label: 'History of Present Illness' },
    { key: 'ros', label: 'Review of Symptoms' },
    { key: 'assessment_plan', label: 'Assessment & Plan' },
    { key: 'additional_mdm', label: 'Additional MDM' },
    { key: 'physical_exam', label: 'Physical Exam' },
    { key: 'after_visit_summary', label: 'Patient Instructions' },
    { key: 'additional_mdm', label: 'Additional MDM'}
  ];
  
  // Determine if we're using SOAP format based on location
  //const isSoapFormat = patient?.location === 'siliconbeach';

  useEffect(() => {
    if (initialRenderRef.current || !isEditing) {
      let textContent = Array.isArray(items) ? items.join('\n') : items;

      // Apply paragraph formatting for HPI and Physical Exam sections
      // This ensures line breaks are shown when editing, not just when viewing
      const isHPI = title === 'History of Present Illness';
      const isPhysicalExam = title === 'Physical Exam';
      const useHPIParagraph = isHPI && userSettings?.hpiFormat === 'paragraph';

      if (useHPIParagraph && textContent) {
        textContent = formatWithInlineHeadings(textContent, HPI_HEADINGS);
      }

      setLocalText(textContent);
      initialRenderRef.current = false;
    }
  }, [items, isEditing, title, userSettings?.hpiFormat]);

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      const len = textareaRef.current.value.length;
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(len, len);
    }
  }, [isEditing]);

  const handleTextChange = (e) => {
    const newText = e.target.value;
    setLocalText(newText);
    setIsDirty(true);

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      const newItems = newText.split('\n');
      onUpdate(title, newItems);
      setIsDirty(false);
    }, 1000);
  };

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const handleBlur = () => {
    if (isDirty) {
      const newItems = localText.split('\n');
      onUpdate(title, newItems);
      setIsDirty(false);
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    }
  };

  const handleClick = (e) => {
    // Don't trigger section click if clicking on upload button or its children
    if (e.target.closest('.upload-container')) {
      return;
    }
    // Don't allow editing in MA View
    if (maView) {
      return;
    }
    onSectionClick(title);
  };
  
  const formatText = (text, section) => {
    if (!text || text === '[]' || text.trim() === '') return '';

    const isHPI = section === 'History of Present Illness';
    const isAssessmentPlan = section === 'Assessment & Plan';
    const isVisitSummary = section === 'Visit Summary';
    const isPhysicalExam = section === 'Physical Exam';
    const isMDMCalculator = section === 'MDM Calculator';
    const useHPIParagraph = isHPI && userSettings.hpiFormat === 'paragraph';
    const useVisitSummarySentence = isVisitSummary && userSettings.visitSummaryFormat === 'sentence';
    const useNumberedAssessmentPlan = isAssessmentPlan && userSettings.numberedAssessmentPlan;

    // For HPI, if paragraph format is enabled, handle special formatting for medical headings
    if (useHPIParagraph) {
      return formatWithInlineHeadings(text, HPI_HEADINGS);
    }

    // For Physical Exam, return as-is (backend already formats correctly)
    if (isPhysicalExam) {
      return text;
    }

    // For MDM Calculator, format with proper paragraphing
    if (isMDMCalculator) {
      const mdmHeadings = [
        'Number and Complexity of Conditions:',
        'Risk of Complications and/or Morbidity or Mortality of Patient Management:',
        'Amount and/or Complexity of Data to be Reviewed and Analyzed:',
        'Number of unique tests ordered this visit:',
        'Number of unique tests results reviewed:'
      ];
      return formatWithInlineHeadings(text, mdmHeadings);
    }

    // For Visit Summary, if sentence format is enabled, join all lines without bullets
    if (useVisitSummarySentence) {
      const lines = text.split('\n');
      const cleanedLines = lines
        .map(line => line.replace(/^[-•*]\s*/, '').trim())
        .filter(line => line.length > 0);
      return cleanedLines.join(' ');
    }

    // For Assessment & Plan, handle numbered format specially
    if (useNumberedAssessmentPlan) {
      return text;
    }

    // For all other sections or when bullet format is enabled
    const lines = text.split('\n');
    const formattedLines = lines.map(line => {
      if (line.trim().startsWith('- ')) {
        return line.replace('- ', '• ');
      }
      return line;
    });
    return formattedLines.join('\n');
  };

    // --- LearnFormatButton with View learned examples ---
    const [showLearnModal, setShowLearnModal] = useState(false);

    // Add a function to handle learning the format (now called from modal)
    const handleLearnFormat = async () => {
      const sectionMap = {
        'History of Present Illness': 'hpi',
        'Review of Symptoms': 'ros',
        'Assessment & Plan': 'assessment_plan',
        'Physical Exam': 'physical_exam',
        'Patient Instructions': 'after_visit_summary',
        'Additional MDM': 'additional_mdm'
      };
      
      const sectionKey = sectionMap[title];
      if (!sectionKey) {
        console.error('Invalid section title:', title);
        return;
      }
    
      setIsLearning(true);
      try {
        const response = await fetch('/api/user/learn-section-style', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            section_key: sectionKey,
            section_content: localText
          }),
        });
    
        const data = await response.json();
        
        if (!response.ok) {
          console.error('Failed to learn format:', data);
          throw new Error(data.error || 'Failed to learn format');
        }
    
        console.log('Format learned successfully:', data);
        setIsLearning(false);
        setIsLearned(true);
        setShowLearnModal(false);
        
        // Refresh the examples after learning
        setTimeout(() => {
          fetchLearnedExamples(sectionKey);
        }, 500);
        
        // Reset to original state after 4 seconds
        setTimeout(() => {
          setIsLearned(false);
        }, 4000);
      } catch (error) {
        console.error('Error learning format:', error);
        setIsLearning(false);
        setIsLearned(false);
        setShowLearnModal(false);
        setUploadError(error.message || 'Failed to learn format');
      }
    };
  
    // --- Add state for learned examples modal ---
    const [showExamplesModal, setShowExamplesModal] = useState(false);
  
    // Fetch learned examples for the selected section

    // 1. Fix the fetchLearnedExamples function (replace the existing one):
    const fetchLearnedExamples = async (sectionOverride) => {
      const sectionKey = sectionOverride || selectedExampleSection;
      if (!sectionKey) return;
      
      try {
        const res = await fetch(`/api/user/learned-notes?section=${sectionKey}`, { 
          credentials: 'include',
          headers: {
            'Accept': 'application/json',
            'Cache-Control': 'no-cache'
          }
        });
        
        if (!res.ok) {
          console.error(`Failed to fetch learned notes: ${res.status}`);
          setLearnedExamples([]);
          return;
        }
        
        const data = await res.json();
        console.log('Fetched learned examples:', data); // Debug log
        
        // Handle different response formats
        if (Array.isArray(data)) {
          setLearnedExamples(data);
        } else if (data.notes && Array.isArray(data.notes)) {
          setLearnedExamples(data.notes);
        } else if (data.examples && Array.isArray(data.examples)) {
          setLearnedExamples(data.examples);
        } else {
          console.error('Unexpected data format:', data);
          setLearnedExamples([]);
        }
      } catch (e) {
        console.error('Error fetching learned examples:', e);
        setLearnedExamples([]);
      }
    };

    // 2. Fix the useEffect that triggers fetchLearnedExamples:
    useEffect(() => {
      if (showExamplesModal || dropdownOpen) {
        // Add a small delay to ensure modal is rendered
        const timer = setTimeout(() => {
          fetchLearnedExamples();
        }, 100);
        return () => clearTimeout(timer);
      }
    }, [showExamplesModal, dropdownOpen, selectedExampleSection]);
  
    // Reset index when modal opens or learnedExamples change
    useEffect(() => {
      if (showExamplesModal) setCurrentExampleIdx(0);
    }, [showExamplesModal, learnedExamples.length]);
  
    // Update selectedExampleSection when modal opens or title changes
    useEffect(() => {
      if (showExamplesModal) {
        setSelectedExampleSection(sectionKeyForTitle(title));
      }
    }, [showExamplesModal, title]);
  
    // Delete example handler
    const handleDeleteExample = async (exampleId) => {
      if (!exampleId) {
        console.error('No example ID provided');
        return;
      }
      
      try {
        const res = await fetch(`/api/user/learned-notes/${exampleId}`, {
          method: 'DELETE',
          credentials: 'include',
          headers: { 'Accept': 'application/json' }
        });
        
        if (!res.ok) {
          console.error('Failed to delete example:', res.status);
          return;
        }
        
        // Remove from local state
        const newExamples = learnedExamples.filter(ex => ex._id !== exampleId);
        setLearnedExamples(newExamples);
        
        // Adjust index if needed
        if (currentExampleIdx >= newExamples.length && newExamples.length > 0) {
          setCurrentExampleIdx(newExamples.length - 1);
        } else if (newExamples.length === 0) {
          setCurrentExampleIdx(0);
        }
      } catch (e) {
        console.error('Error deleting example:', e);
      }
    };
  
    // Main button logic
    let buttonStyle = 'bg-white-100 hover:bg-gray-200 text-gray-700 border border-gray-200';
    let buttonText = 'Learn Format';
    let buttonDisabled = false;
  
    const isNoteEmpty = !localText || localText.trim() === '';
    if (isLearning) {
      buttonStyle = 'bg-teal-100 text-teal-700';
      buttonText = 'Learning...';
      buttonDisabled = true;
    } else if (isLearned) {
      buttonStyle = 'bg-green-100 text-green-700';
      buttonText = 'Format learned!';
      buttonDisabled = true;
    } else if (!useFormatted) {
      buttonStyle = 'bg-gray-200 text-gray-400 cursor-not-allowed border border-gray-200';
      buttonText = 'Learn Format';
      buttonDisabled = true;
    } else if (isNoteEmpty) {
      buttonStyle = 'bg-gray-200 text-gray-400 cursor-not-allowed border border-gray-200';
      buttonText = 'Learn Format';
      buttonDisabled = true;
    } else {
      // Default state when in formatted mode and note is not empty
      buttonStyle = 'bg-white-100 hover:bg-gray-200 text-gray-700 border border-gray-200';
      buttonText = 'Learn Format';
      buttonDisabled = false;
    }
  
    const handleDropdownClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDropdownOpen(prev => !prev);
    };
  
    const handleSwitchFormat = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setUseFormatted(!useFormatted);
      setDropdownOpen(false);
    };
  
    const handleViewExamples = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setShowExamplesModal(true);
      setDropdownOpen(false);
    };
  
    // Add a function to get the preview text based on useFormatted
    const getPreviewText = () => {
      if (!useFormatted) {
        // Show plain text, no formatting
        return Array.isArray(items) ? items.join('\n') : items;
      } else {
        // Use the formatted text
        return formatText(Array.isArray(items) ? items.join('\n') : items, title);
      }
    };
  
    // Close dropdown when clicking outside
    useEffect(() => {
      if (!dropdownOpen) return;
      function handleClickOutside(event) {
        if (
          dropdownMenuRef.current &&
          !dropdownMenuRef.current.contains(event.target) &&
          buttonRef.current &&
          !buttonRef.current.contains(event.target)
        ) {
          setDropdownOpen(false);
        }
      }
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [dropdownOpen]);

  const handleFileUpload = async (file) => {
    setIsUploading(true);
    setUploadError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('section', sectionKeyForTitle(title));  // Add section info

      const response = await fetch('/api/user/upload-document', {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upload file');
      }

      const data = await response.json();
      setUploadedFile({
        name: data.document.name,
        size: data.document.size,
        url: data.document.url,
        uploaded_at: new Date().toISOString()
      });
      setShowUploadModal(false);
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError(error.message || 'Failed to upload file. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveFile = async () => {
    try {
      const response = await fetch('/api/user/reference-document', {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to delete file');
      }

      setUploadedFile(null);
    } catch (error) {
      console.error('Error deleting file:', error);
      setFileError('Failed to delete file. Please try again.');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleUploadClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  // Load reference document on mount
  useEffect(() => {
    const loadReferenceDocument = async () => {
      try {
        const response = await fetch('/api/user/reference-document', {
          credentials: 'include'
        });
        
        if (response.ok) {
          const data = await response.json();
          // Only set the uploaded file if this is the section the document belongs to
          if (data.section === sectionKeyForTitle(title)) {
            setUploadedFile({
              name: data.name,
              size: data.size || 0,
              url: data.url,
              uploaded_at: data.upload_date
            });
          }
        }
      } catch (error) {
        console.error('Error loading reference document:', error);
      }
    };

    loadReferenceDocument();
  }, [title]); // Add title as a dependency

  return (
    <section
      className={`relative bg-white cursor-text px-3 hover:bg-gray-50 transition-colors ${
        isCompact ? 'py-2' : 'py-3'
      }`}
      onClick={handleClick}
    >
      {/* ── 1. Header ─────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3
            className={`text-lg px-2 font-medium ${
              isVisitSummary ? 'text-gray-500' : 'text-teal-600'
            } ${isCompact ? 'mb-1' : 'mb-2'}`}
          >
            {title}:
          </h3>
          {customSectionId && onDeleteSection && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDeleteSection(customSectionId);
              }}
              //className="text-red-400 hover:text-red-700 pb-1.5 pl-3 rounded"
              className="pb-1.5 pl-3 text-red-300 rounded-md hover:text-red-200 font-medium flex items-center gap-2 disabled:opacity-5"
              title="Delete section"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
  
      {/* ── 2. Body (textarea or preview) ─────────────────── */}
      <div className={isCompact ? 'mt-1' : 'mt-2'}>
        {isEditing ? (
          <textarea
            ref={textareaRef}
            value={
              !useFormatted ? (Array.isArray(items) ? items.join('\n') : items) : localText
            }
            onChange={handleTextChange}
            onBlur={handleBlur}
            onClick={(e) => e.stopPropagation()}
            readOnly={maView || !useFormatted}
            className={`w-full p-2 border rounded-md text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none overflow-y-auto ${
              isCompact ? 'min-h-[230px]' : 'min-h-[350px]'
            } ${contentHeight} ${maView ? 'bg-gray-100 cursor-not-allowed' : ''}`}
            placeholder={`Enter ${title.toLowerCase()}`}
          />
        ) : (
          <div
            className={`whitespace-pre-wrap text-gray-700 overflow-y-auto px-2 ${contentHeight}`}
            dangerouslySetInnerHTML={{
              __html: getPreviewText().replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
            }}
          />
        )}
      </div>
  
      {/* ── 3. Actions row (Learn, Style, Upload) ─────────── */}
      {/* ── Action bar: Learn ▸ Style ▸ Upload ───────────────────────── */}
      {!isVisitSummary && !isSoapFormat && !maView && (
        <div className="mt-3 flex flex-wrap items-center space-x-2" onClick={(e) => e.stopPropagation()}>
          {/* 1️⃣  Learn-format button (+ caret dropdown) */}
          <div className="relative" ref={buttonRef}>
            <div className="flex">
              {/* main button */}
              <button
                disabled={buttonDisabled}
                className={`px-2 py-1 text-sm rounded-md ${buttonStyle}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!buttonDisabled) setShowLearnModal(true);
                }}
              >
                {buttonText}
              </button>

              {/* caret for dropdown */}
              <button
                type="button"
                aria-haspopup="true"
                aria-expanded={dropdownOpen}
                className="ml-1 px-1 py-1 rounded-md border border-gray-200 bg-white hover:bg-gray-100"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setDropdownOpen((prev) => !prev);
                }}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>

            {/* dropdown menu */}
            {dropdownOpen && (
              <div
                ref={dropdownMenuRef}
                className="absolute z-50 mt-2 min-w-[220px] bg-white border border-gray-200 rounded-md shadow-lg"
                onClick={(e) => e.stopPropagation()}
              >
                {!isNoteEmpty && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      setUseFormatted((prev) => !prev);
                      setDropdownOpen(false);
                    }}
                    className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                  >
                    {useFormatted ? 'Formatting Off' : 'Formatting On'}
                  </button>
                )}
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    setShowExamplesModal(true);
                    setDropdownOpen(false);
                  }}
                  className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                >
                  View learned formats
                </button>
              </div>
            )}
          </div>

          {/* 2️⃣  Section-style picker */}
          <SectionStyleSelector
            selectedStyle={sectionStyle}
            onStyleChange={onSectionStyleChange}
            sectionKey={sectionKey}
          />
          {/* UserPromptSelector with proper spacing */}
          <div className="ml-2">
            <UserPromptSelector
              sectionKey={sectionKey}
              sectionTitle={title}  // ADD THIS LINE
              onPromptChange={(sectionKey, prompt) => {
                // Handle prompt change if needed
                console.log(`Prompt updated for ${sectionKey}:`, prompt);
              }}
            />
          </div>

          {/* SendWithSelector - available when enabled */}
          {shouldShowSendWithSelector && (
            <div className="ml-2">
              <SendWithSelector
                selectedSendWith={sendWithValue}
                onSendWithChange={onSendWithChange}
                sectionKey={sectionKey}
              />
            </div>
          )}

          {/* 3️⃣  Upload reference document */}
          {!isEditing && (
            <button
              className="px-2 py-1 text-sm rounded-md bg-white hover:bg-gray-100 border border-gray-200"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowUploadModal(true);
              }}
            >
              <span>{uploadedFile ? 'New Upload' : 'Upload'}</span>
            </button>
          )}
        </div>
      )}
  
      {/* ── 4. Copy button ───────────────────────────────── */}
      {!isEditing && <CopyButton textToCopy={localText} ariaLabel={`Copy ${title}`} />}
  
      {/* ── 5. Upload modals (normal + progress) ─────────── */}
      <UploadModal
        isOpen={showUploadModal}
        onClose={() => {
          setShowUploadModal(false);
          setUploadError(null);
        }}
        onUpload={handleFileUpload}
        isUploading={isUploading}
        uploadError={uploadError}
        uploadedFile={uploadedFile}
        onRemoveFile={handleRemoveFile}
      />
  
      {isUploading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg max-w-lg w-full p-6">
            <div className="flex flex-col items-center">
              <svg className="animate-spin h-8 w-8 text-teal-600 mb-2" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" strokeWidth="4" stroke="currentColor" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <p className="text-gray-600">Uploading document...</p>
              {uploadError && <p className="mt-2 text-sm text-red-600">{uploadError}</p>}
            </div>
          </div>
        </div>
      )}
      {/* Learn Format Modal */}
      {showLearnModal && (!isSoapFormat || title === "Patient Instructions") && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40" onClick={(e) => e.stopPropagation()}>
          <div className="bg-white rounded-lg shadow-lg max-w-lg w-full p-6 relative">
            <button
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
              onClick={(e) => { e.stopPropagation(); setShowLearnModal(false); }}
            >✕</button>
            <p className="mb-4 text-teal-600 mt-4">Teach Preface to learn the headings and structure of this section. For best results, repeat this process on several notes until you see consistent formatting.</p>
            <div className="mb-2 font-semibold flex items-center justify-between">
              <span>Preface will learn this example:</span>
              {learnedExamples.length > 0 && (
                <span className="text-xs text-gray-500 font-normal">Examples learned: {learnedExamples.length}</span>
              )}
            </div>
            <div className="bg-gray-100 rounded p-4 text-gray-700 mb-6 max-h-64 overflow-y-auto">
              <span className="font-semibold text-gray-600">{title}</span>
              <div className="whitespace-pre-line mt-1" style={{fontFamily: 'inherit'}}>{getPreviewText()}</div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                className="px-4 py-2 rounded bg-gray-200 text-gray-700 hover:bg-gray-300"
                onClick={() => setShowLearnModal(false)}
              >Cancel</button>
              <button
                className="px-4 py-2 rounded bg-teal-600 text-white hover:bg-teal-700 font-semibold"
                onClick={handleLearnFormat}
                disabled={isLearning}
              >{isLearning ? 'Learning...' : 'Learn Format'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Learned Examples Modal - EXACT WORKING VERSION */}
      {showExamplesModal && (!isSoapFormat || title === "Patient Instructions") && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40" onClick={(e) => e.stopPropagation()}>
          <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full min-h-[500px] max-h-[650px] p-6 relative flex flex-col">
            <button
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
              onClick={(e) => { e.stopPropagation(); setShowExamplesModal(false); }}
            >✕</button>
            <div className="flex items-center justify-between mb-5 mt-5">
              <h2 className="text-2xl font-semibold">Learned Examples</h2>
              <select
                className="border rounded px-2 py-1 text-gray-700"
                value={selectedExampleSection}
                onChange={e => {
                  setSelectedExampleSection(e.target.value);
                  fetchLearnedExamples(e.target.value);
                  setCurrentExampleIdx(0);
                }}
              >
                {sectionOptions.map(opt => (
                  <option key={opt.key} value={opt.key}>{opt.label}</option>
                ))}
              </select>
            </div>
            {learnedExamples.length === 0 ? (
              <p className="text-gray-500 text-center py-8 flex-1">No examples have been learned yet.</p>
            ) : (
              <div className="space-y-4 flex-1 flex flex-col">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-sm text-gray-500">
                    Example {currentExampleIdx + 1} of {learnedExamples.length}
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCurrentExampleIdx(prev => Math.max(0, prev - 1))}
                      disabled={currentExampleIdx === 0}
                      className="px-3 py-1 rounded bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCurrentExampleIdx(prev => Math.min(learnedExamples.length - 1, prev + 1))}
                      disabled={currentExampleIdx === learnedExamples.length - 1}
                      className="px-3 py-1 rounded bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
                <div className="bg-gray-100 rounded p-4 text-gray-700 h-[400px] overflow-y-auto">
                  <div className="whitespace-pre-line" style={{fontFamily: 'inherit'}}>
                    {learnedExamples[currentExampleIdx]?.example_content}
                  </div>
                </div>
                <div className="flex justify-between items-center text-sm text-gray-500 mt-2">
                  <span>Learned on {new Date(learnedExamples[currentExampleIdx]?.created_at).toLocaleDateString()}</span>
                  <button
                    onClick={() => handleDeleteExample(learnedExamples[currentExampleIdx]._id)}
                    className="text-red-500 hover:text-red-700"
                  >
                    Delete Example
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      
    </section>
  );
}
export default EditableSection;

const DeleteConfirmDialog = ({ isOpen, onClose, onConfirm, patientName, isDeleting }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 relative">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
          disabled={isDeleting}
        >
          <X className="w-5 h-5" />
        </button>

        <h2 className="text-xl font-semibold mb-4">Delete Patient Record</h2>
        <p className="text-gray-600 mb-6">
          Are you sure you want to delete {patientName}'s record? This action cannot be undone.
        </p>

        <div className="flex justify-end space-x-4">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium disabled:opacity-50"
            disabled={isDeleting}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 font-medium flex items-center gap-2 disabled:opacity-50"
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4" />
                Delete
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const Timer = ({ isRecording }) => {
  const [time, setTime] = useState(0);

  useEffect(() => {
    let interval;
    if (isRecording) {
      interval = setInterval(() => {
        setTime(prevTime => prevTime + 1);
      }, 1000);
    } else {
      setTime(0);
    }
    return () => clearInterval(interval);
  }, [isRecording]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  return (
    <span className="text-sm font-medium">
      {formatTime(time)}
    </span>
  );
};

// Changed props list to remove language selection props
const FloatingControls = ({
  isRecording,
  isTranscribing,
  startRecording,
  stopRecording,
  handleSendToEHR,
  isSendingToEHR,
  showSentSuccess,
  patient,
  detectedLanguage,
  ehrErrorStatus,
  ehrCooldownNotice,
  isEhrCooldown,
  maView = false
}) => {
  // Don't show floating controls in MA View
  if (maView) {
    return null;
  }

  const isSendBlocked = isSendingToEHR || isTranscribing || isRecording;
  const sendButtonClassName = `px-4 py-2 text-white rounded-md w-full sm:w-auto ${
    isSendBlocked || isEhrCooldown
      ? 'bg-gray-400 cursor-not-allowed'
      : 'bg-teal-500 hover:bg-teal-600'
  }`;

  return (
    <div className="fixed bottom-4 inset-x-0 sm:right-32 sm:left-auto sm:bottom-10">
      <div className="flex flex-col items-center sm:items-end sm:flex-row sm:space-x-12 space-y-4 sm:space-y-0 px-4 sm:px-0 w-full max-w-md mx-auto sm:w-auto">
        {/* Record Button */}
        <div className="flex items-center w-full sm:w-auto">
          {isTranscribing ? (
            <div className="flex items-center space-x-2 text-gray-600 w-full justify-center">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-sm sm:text-base">Transcribing...</span>
            </div>
          ) : (
            <div className="flex items-center space-x-3 w-full">
              {/* Language selector UI - Moved to left of record button */}
              {isRecording && detectedLanguage && (
                <div className="px-3 py-1 bg-blue-50 text-blue-700 text-xs rounded-md border border-blue-100 flex items-center">
                  <Globe className="w-3 h-3 mr-1" />
                  <span>Detected: {detectedLanguage === 'en' ? 'English' :
                                  detectedLanguage === 'es' ? 'Spanish' :
                                  detectedLanguage === 'fr' ? 'French' :
                                  detectedLanguage === 'ru' ? 'Russian' :
                                  detectedLanguage === 'de' ? 'German' :
                                  detectedLanguage === 'it' ? 'Italian' :
                                  detectedLanguage === 'pt' ? 'Portuguese' :
                                  detectedLanguage === 'fa' ? 'Persian' :
                                  detectedLanguage}</span>
                </div>
              )}

              <button
                onClick={isRecording ? stopRecording : startRecording}
                className={`px-4 py-2 rounded-md flex-1 sm:flex-none flex items-center justify-center space-x-2 ${
                  isRecording
                    ? 'bg-red-500 hover:bg-red-600 text-white'
                    : 'bg-teal-500 hover:bg-teal-600 text-white'
                }`}
                disabled={isTranscribing}
              >
                {isRecording ? (
                  <>
                    <Square className="w-5 h-5" />
                    <span>Stop</span>
                    <span className="ml-2 pl-2 border-l border-white/30">
                      <Timer isRecording={isRecording} />
                    </span>
                  </>
                ) : (
                  <>
                    <Mic className="w-5 h-5" />
                    <span>Record</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Send to EHR Button */}
        {patient?.has_soap_note && (
          <div className="flex items-center w-full sm:w-auto">
            {ehrErrorStatus ? (
              <div className="px-4 py-2 bg-red-500 text-white rounded-md flex items-center justify-center w-full sm:w-auto">
                Patient Encounter not Set
              </div>
            ) : showSentSuccess ? (
              <div className="px-4 py-2 bg-green-500 text-white rounded-md flex items-center justify-center w-full sm:w-auto">
                Sent!
              </div>
            ) : (
              <div className="relative w-full sm:w-auto">
                {ehrCooldownNotice && (
                  <div
                    className="absolute -top-10 left-1/2 -translate-x-1/2 rounded-md bg-gray-900 text-white text-xs px-3 py-1 shadow-lg z-50 whitespace-nowrap"
                    role="status"
                    aria-live="polite"
                  >
                    {ehrCooldownNotice}
                  </div>
                )}
                <button
                  onClick={handleSendToEHR}
                  disabled={isSendBlocked}
                  aria-disabled={isSendBlocked || isEhrCooldown}
                  className={sendButtonClassName}
                >
                  {isSendingToEHR ? 'Sending...' : 'Finish patient & Send to EHR'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};


const SuggestedTestsSection = ({ tests, userSettings = {} }) => {
  // Only render if suggestPOCTests setting is enabled and there are tests
  if (!userSettings.suggestPOCTests) return null;
  
  // Single test copy button component
  const TestCopyButton = ({ test }) => {
    const [copied, setCopied] = useState(false);
    
    const handleCopy = () => {
      navigator.clipboard.writeText(test)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        })
        .catch((err) => {
          console.error('Failed to copy test: ', err);
        });
    };
    
    return (
      <button
        onClick={handleCopy}
        className="ml-2 text-gray-500 hover:text-gray-700 transition-colors"
        aria-label={`Copy test: ${test}`}
      >
        {copied ? (
          <svg className="h-4 w-4 text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        ) : (
          <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        )}
      </button>
    );
  };

  return (
    <section className="relative bg-white px-3 py-2 hover:bg-gray-50 transition-colors">
      <div className="px-2">
        <h3 className="text-lg font-medium text-gray-500 mb-2">
          Suggested POC Tests:
        </h3>
        <div className="whitespace-pre-wrap text-gray-700 overflow-y-auto max-h-64 pl-4">
          {tests && tests.length > 0 ? (
            <ul className="space-y-2">
              {tests.map((test, index) => (
                <li key={index} className="flex items-center">
                  <span>{test}</span>
                  <TestCopyButton test={test} />
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-500 italic">No suggested tests available</p>
          )}
        </div>
      </div>
    </section>
  );
};

const ICDCodesSection = ({ codes, userSettings = {} }) => {
  // Only render if showICDCodes setting is enabled
  if (!userSettings.showICDCodes) return null;
  
  // Parse the codes into an array of individual codes
  const parseCodesArray = () => {
    // If no codes or empty array, return empty array
    if (!codes || (Array.isArray(codes) && codes.length === 0)) {
      return [];
    }
    
    // If it's already an array of multiple items, use it directly
    if (Array.isArray(codes) && codes.length > 1) {
      return codes;
    }
    
    // Process codes into a consistent string format
    let codesString = '';
    
    if (Array.isArray(codes) && codes.length === 1) {
      // Array with one element - use that element
      codesString = codes[0];
    } else if (typeof codes === 'string') {
      // String - use directly
      codesString = codes;
    } else {
      // Unknown format
      return [];
    }
    
    // Handle case where codes might be an empty string
    if (!codesString || codesString.trim() === '') {
      return [];
    }
    
    // Parse ICD codes, properly handling nested parentheses
    const results = [];
    
    // Define a regex that finds the start of an ICD code (letter followed by numbers)
    const icdStartPattern = /\b([A-Z][0-9]+(?:\.[0-9]+)?)\s*\(/g;
    let match;
    
    while ((match = icdStartPattern.exec(codesString)) !== null) {
      const codeStart = match.index;
      const code = match[1]; // The actual code like "I10"
      
      // Find the matching closing parenthesis, accounting for nested parentheses
      let openParens = 1;
      let descEnd = match.index + match[0].length;
      
      while (openParens > 0 && descEnd < codesString.length) {
        if (codesString[descEnd] === '(') openParens++;
        if (codesString[descEnd] === ')') openParens--;
        descEnd++;
      }
      
      // If we found a proper closing parenthesis
      if (openParens === 0) {
        const fullCodeWithDesc = codesString.substring(codeStart, descEnd).trim();
        results.push(fullCodeWithDesc);
        
        // Update the regex lastIndex to avoid finding partial matches within this code
        icdStartPattern.lastIndex = descEnd;
      }
    }
    
    // If we found parsed codes, return them
    if (results.length > 0) {
      return results;
    }
    
    // Fallback if parsing fails - return the original string as a single item
    return [codesString];
  };
  
  // Get the parsed array of codes
  const codeArray = parseCodesArray();
  
  // Single ICD code copy button component
  const ICDCodeCopyButton = ({ code }) => {
    const [copied, setCopied] = useState(false);
    
    // Extract just the code (before the parenthesis)
    const extractCodeOnly = (codeWithDescription) => {
      // Use a more specific regex to match ICD code patterns
      const match = codeWithDescription.match(/^([A-Z][0-9]+(?:\.[0-9]+)?)/);
      return match ? match[1] : codeWithDescription;
    };
    
    const codeOnly = extractCodeOnly(code);
    
    const handleCopy = () => {
      navigator.clipboard.writeText(codeOnly)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        })
        .catch((err) => {
          console.error('Failed to copy code: ', err);
        });
    };
    
    return (
      <button
        onClick={handleCopy}
        className="ml-2 text-gray-500 hover:text-gray-700 transition-colors"
        aria-label={`Copy ICD code ${codeOnly}`}
        title={`Copy code: ${codeOnly}`}
      >
        {copied ? (
          <svg className="h-4 w-4 text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        ) : (
          <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        )}
      </button>
    );
  };

  return (
    <section className="relative bg-white px-3 py-2 hover:bg-gray-50 transition-colors">
      <div className="px-2">
        <h3 className="text-lg font-medium text-gray-500 mb-2">
          ICD-10 Codes:
        </h3>
        <div className="whitespace-pre-wrap text-gray-700 overflow-y-auto max-h-64 pl-4">
          {codeArray && codeArray.length > 0 ? (
            <ul className="space-y-2 list-none">
              {codeArray.map((code, index) => (
                <li key={index} className="flex items-center py-1">
                  <span>{code}</span>
                  <ICDCodeCopyButton code={code} />
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-500 italic">No ICD codes available</p>
          )}
        </div>
      </div>
    </section>
  );
};

const EMCodesSection = ({ codes, userSettings = {}, onInfoClick }) => {
  // Only render if showEMCodes setting is enabled
  if (!userSettings.showEMCodes) return null;

  // Parse the codes into an array
  const parseCodesArray = () => {
    // If no codes or empty array, return empty array
    if (!codes || (Array.isArray(codes) && codes.length === 0)) {
      return [];
    }

    // If it's already an array of multiple items, use it directly
    if (Array.isArray(codes) && codes.length > 1) {
      return codes;
    }

    // Process codes into a consistent string format
    let codesString = '';

    if (Array.isArray(codes) && codes.length === 1) {
      // Array with one element - use that element
      codesString = codes[0];
    } else if (typeof codes === 'string') {
      // String - use directly
      codesString = codes;
    } else {
      // Unknown format
      return [];
    }

    // Handle case where codes might be an empty string or N/A
    if (!codesString || codesString.trim() === '' || codesString.trim() === 'N/A') {
      return [];
    }

    // Return the code as a single item array
    return [codesString];
  };

  // Get the parsed array of codes
  const codeArray = parseCodesArray();

  // Single E/M code copy button component
  const EMCodeCopyButton = ({ code }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
      navigator.clipboard.writeText(code)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        })
        .catch((err) => {
          console.error('Failed to copy code: ', err);
        });
    };

    return (
      <button
        onClick={handleCopy}
        className="ml-2 text-gray-500 hover:text-gray-700 transition-colors"
        aria-label={`Copy E/M code ${code}`}
        title={`Copy code: ${code}`}
      >
        {copied ? (
          <svg className="h-4 w-4 text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        ) : (
          <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        )}
      </button>
    );
  };

  return (
    <section className="relative bg-white px-3 py-2 hover:bg-gray-50 transition-colors">
      <div className="px-2">
        <div className="flex items-center mb-2">
          <h3 className="text-lg font-medium text-gray-500">
            E/M Code:
          </h3>
          {onInfoClick && (
            <button
              onClick={onInfoClick}
              className="ml-2 text-gray-400 hover:text-gray-600 transition-all hover:scale-110"
              aria-label="View E/M Education"
              title="View explanation for this E/M code"
            >
              <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm8.706-1.442c1.146-.573 2.437.463 2.126 1.706l-.709 2.836.042-.02a.75.75 0 01.67 1.34l-.04.022c-1.147.573-2.438-.463-2.127-1.706l.71-2.836-.042.02a.75.75 0 11-.671-1.34l.041-.022zM12 9a.75.75 0 100-1.5.75.75 0 000 1.5z" clipRule="evenodd" />
              </svg>
            </button>
          )}
        </div>
        <div className="text-gray-700 overflow-y-auto max-h-64 pl-4">
          {codeArray && codeArray.length > 0 ? (
            <div className="space-y-2">
              {codeArray.map((code, index) => (
                <div key={index} className="relative">
                  <div className="pr-10">
                    {renderMarkdown(code)}
                  </div>
                  <div className="absolute top-0 right-0">
                    <EMCodeCopyButton code={code} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 italic">No E/M code available</p>
          )}
        </div>
      </div>
    </section>
  );
};

const EMEducationSection = React.forwardRef(({ education, userSettings = {} }, ref) => {
  // Only render if showEMCodes setting is enabled
  if (!userSettings.showEMCodes) return null;

  // Parse the education content
  const parseEducation = () => {
    // If no education or empty array, return empty string
    if (!education || (Array.isArray(education) && education.length === 0)) {
      return '';
    }

    // Process education into a consistent string format
    let educationString = '';

    if (Array.isArray(education) && education.length === 1) {
      // Array with one element - use that element
      educationString = education[0];
    } else if (Array.isArray(education) && education.length > 1) {
      // Array with multiple elements - join them
      educationString = education.join('\n\n');
    } else if (typeof education === 'string') {
      // String - use directly
      educationString = education;
    } else {
      // Unknown format
      return '';
    }

    // Handle case where education might be an empty string or N/A
    if (!educationString || educationString.trim() === '' || educationString.trim() === 'N/A') {
      return '';
    }

    return educationString;
  };

  const educationContent = parseEducation();

  return (
    <section ref={ref} className="relative bg-white px-3 py-2 hover:bg-gray-50 transition-colors">
      <div className="px-2">
        <h3 className="text-lg font-medium text-gray-500 mb-1">
          E/M Education:
        </h3>
        <h3 className="text-md font-light italic text-gray-500 mb-2">
          Additional MDM from Preface may be appended to substantiate higher E/M complexity
        </h3>
        <div className="text-gray-700 overflow-y-auto max-h-96 pl-4">
          {educationContent ? (
            <div className="text-gray-700">
              {renderMarkdown(educationContent)}
            </div>
          ) : (
            <p className="text-gray-500 italic">No E/M education available</p>
          )}
        </div>
      </div>
    </section>
  );
});

const CPTCodesSection = ({ codes, userSettings = {}, currentLocation, patientLocation }) => {
  if (!userSettings.showCPTCodes) {
    return null;
  }
  
  // Parse the codes into an array of individual codes
  const parseCodesArray = () => {
    // If no codes or empty array, return empty array
    if (!codes || (Array.isArray(codes) && codes.length === 0)) {
      return [];
    }
    
    // If it's already an array of multiple items, use it directly
    if (Array.isArray(codes) && codes.length > 1) {
      return codes;
    }
    
    // Convert to string for parsing
    let codesString = '';
    if (Array.isArray(codes) && codes.length === 1) {
      codesString = codes[0];
    } else if (typeof codes === 'string') {
      codesString = codes;
    } else {
      // Unknown format
      return [];
    }
    
    // Handle case where codes might be an empty string
    if (!codesString || codesString.trim() === '') {
      return [];
    }
    
    // Parse CPT codes, properly handling nested parentheses
    const results = [];
    
    // Define a regex that finds the start of a CPT code (5 digits)
    const cptStartPattern = /\b(\d{5})\s*\(/g;
    let match;
    
    while ((match = cptStartPattern.exec(codesString)) !== null) {
      const codeStart = match.index;
      const code = match[1]; // The actual code like "99213"
      
      // Find the matching closing parenthesis, accounting for nested parentheses
      let openParens = 1;
      let descEnd = match.index + match[0].length;
      
      while (openParens > 0 && descEnd < codesString.length) {
        if (codesString[descEnd] === '(') openParens++;
        if (codesString[descEnd] === ')') openParens--;
        descEnd++;
      }
      
      if (openParens === 0) {
        // Extract the full code with description
        const fullCode = codesString.substring(codeStart, descEnd);
        results.push(fullCode);
      }
    }
    
    if (results.length > 0) {
      return results;
    }
    
    // Fallback if parsing fails - return the original string as a single item
    return [codesString];
  };
  
  // Get the parsed array of codes
  const codeArray = parseCodesArray();
  
  // Single CPT code copy button component
  const CPTCodeCopyButton = ({ code }) => {
    const [copied, setCopied] = useState(false);
    
    // Extract just the code (before the parenthesis)
    const extractCodeOnly = (codeWithDescription) => {
      // Use a more specific regex to match CPT code patterns
      const match = codeWithDescription.match(/^(\d{5})/);
      return match ? match[1] : codeWithDescription;
    };
    
    const codeOnly = extractCodeOnly(code);
    
    const handleCopy = () => {
      navigator.clipboard.writeText(codeOnly)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        })
        .catch((err) => {
          console.error('Failed to copy code: ', err);
        });
    };
    
    return (
      <button
        onClick={handleCopy}
        className="ml-2 text-gray-500 hover:text-gray-700 transition-colors"
        aria-label={`Copy CPT code ${codeOnly}`}
        title={`Copy code: ${codeOnly}`}
      >
        {copied ? (
          <svg className="h-4 w-4 text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        ) : (
          <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 002 2z" />
          </svg>
        )}
      </button>
    );
  };

  return (
    <section className="relative bg-white px-3 py-2 hover:bg-gray-50 transition-colors">
      <div className="px-2">
        <h3 className="text-lg font-medium text-gray-500 mb-2">
          CPT Codes:
        </h3>
        <div className="whitespace-pre-wrap text-gray-700 overflow-y-auto max-h-64 pl-4">
          {codeArray && codeArray.length > 0 ? (
            <ul className="space-y-2 list-none">
              {codeArray.map((code, index) => (
                <li key={index} className="flex items-center py-1">
                  <span>{code}</span>
                  <CPTCodeCopyButton code={code} />
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-gray-500 italic">
              <p>No CPT codes available</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

// MA Section component for All Valley (Calimesa) Medical Assistants
const MASection = ({
  medications = [],
  inhouseLabs = [],
  sendoutLabs = [],
  procedures = [],
  patientLocation = '',
  onSave,
  isEditable = false
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [drafts, setDrafts] = useState({
    medications: '',
    inhouseLabs: '',
    sendoutLabs: '',
    procedures: ''
  });

  // Only render if patient is calimesa
  if (patientLocation?.toLowerCase() !== 'calimesa') {
    return null;
  }

  // Helper function to parse and format array content
  const formatContent = (content) => {
    if (!content) return 'None';
    if (Array.isArray(content)) {
      const text = content.join('\n').trim();
      if (!text || text.toLowerCase() === 'none') return 'None';
      return text;
    }
    if (typeof content === 'string') {
      const text = content.trim();
      if (!text || text.toLowerCase() === 'none') return 'None';
      return text;
    }
    return 'None';
  };

  const formatDraftValue = (content) => {
    if (!content) return '';
    if (Array.isArray(content)) {
      const text = content.join('\n').trim();
      return text.toLowerCase() === 'none' ? '' : text;
    }
    if (typeof content === 'string') {
      const text = content.trim();
      return text.toLowerCase() === 'none' ? '' : text;
    }
    return '';
  };

  const startEditing = () => {
    setDrafts({
      medications: formatDraftValue(medications),
      inhouseLabs: formatDraftValue(inhouseLabs),
      sendoutLabs: formatDraftValue(sendoutLabs),
      procedures: formatDraftValue(procedures)
    });
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleSave = () => {
    if (!onSave) {
      setIsEditing(false);
      return;
    }

    const toArray = (value) => (value || '').split('\n');

    onSave({
      ma_medications: toArray(drafts.medications),
      ma_inhouse_labs: toArray(drafts.inhouseLabs),
      ma_sendout_labs: toArray(drafts.sendoutLabs),
      ma_procedures: toArray(drafts.procedures)
    });

    setIsEditing(false);
  };

  const handleDraftChange = (key) => (event) => {
    const { value } = event.target;
    setDrafts((prev) => ({ ...prev, [key]: value }));
  };

  const renderField = (label, value) => {
    const formatted = formatContent(value);
    const isEmpty = formatted === 'None';

    return (
      <li className="flex items-start py-1">
        <span className="font-bold">{label}:</span>
        <span className={`ml-2 whitespace-pre-wrap ${isEmpty ? 'text-gray-500 italic' : ''}`}>
          {formatted}
        </span>
      </li>
    );
  };

  const renderEditorField = (label, fieldKey, rows = 2) => (
    <li className="flex flex-col">
      <span className="font-bold">{label}:</span>
      <textarea
        value={drafts[fieldKey]}
        onChange={handleDraftChange(fieldKey)}
        rows={rows}
        className="mt-1 w-full p-2 border rounded-md text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
        placeholder="None"
      />
    </li>
  );

  return (
    <section className="relative bg-white px-3 py-2 hover:bg-gray-50 transition-colors">
      <div className="px-2">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-medium text-gray-500">
            MA Section:
          </h3>
          {isEditable && (
            isEditing ? (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleSave}
                  className="px-2 py-1 text-xs rounded-md bg-teal-500 text-white hover:bg-teal-600"
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-2 py-1 text-xs rounded-md bg-gray-200 text-gray-700 hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={startEditing}
                className="px-2 py-1 text-xs rounded-md text-gray-600 hover:text-gray-800 hover:bg-gray-100"
              >
                Edit
              </button>
            )
          )}
        </div>
        <div className={`text-gray-700 overflow-y-auto max-h-64 pl-4 ${isEditing ? '' : 'whitespace-pre-wrap'}`}>
          {isEditing ? (
            <ul className="space-y-3 list-none">
              {renderEditorField('Medications', 'medications')}
              {renderEditorField('In-House Labs', 'inhouseLabs')}
              {renderEditorField('Send-Out Labs', 'sendoutLabs')}
              {renderEditorField('Procedures', 'procedures', 3)}
            </ul>
          ) : (
            <ul className="space-y-2 list-none">
              {renderField('Medications', medications)}
              {renderField('In-House Labs', inhouseLabs)}
              {renderField('Send-Out Labs', sendoutLabs)}
              {renderField('Procedures', procedures)}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
};

export const PatientDetail = ({ patient, onPatientUpdate, onEditingStateChange, onDeletePatient, onEHRStatusChange, userSettings = {}, onMultiPatientsDetected,  // Add this prop
  onPatientListUpdated, currentLocation, saveSettingsMutation, onRecordingStateChange }) => {
  const [activeSection, setActiveSection] = useState(null);
  const [editedData, setEditedData] = useState({
    visit_summary: [],
    // Standard format fields
    hpi: '',
    assessment_plan: '',
    ros: '',
    // SOAP format fields
    subjective: '',
    objective: '',
    assessment: '',
    plan: '',
    // Common fields
    suggested_tests: [],
    icd_codes: [],
    cpt_codes: [],
    em_codes: [],
    em_education: [],
    physical_exam: [],
    additional_mdm: [],
    mdm_calculator: [],
    after_visit_summary: [],
    // MA Section fields
    ma_medications: [],
    ma_inhouse_labs: [],
    ma_sendout_labs: [],
    ma_procedures: []
  });
  const [isSendingToEHR, setIsSendingToEHR] = useState(false);
  const [error, setError] = useState(null);
  const EHR_COOLDOWN_MS = 30000;
  const [ehrCooldownUntil, setEhrCooldownUntil] = useState(null);
  const ehrCooldownUntilRef = useRef(null);
  const ehrCooldownTimeoutRef = useRef(null);
  const [ehrCooldownNotice, setEhrCooldownNotice] = useState('');
  const ehrCooldownNoticeTimeoutRef = useRef(null);
  const containerRef = useRef(null);
  const emEducationRef = useRef(null);
  const emEducationRefsMap = useRef({});
  const [showSentSuccess, setShowSentSuccess] = useState(false);
  const prevPatientRef = useRef();
  const [validationErrors, setValidationErrors] = useState({});
  const [isEditingInfo, setIsEditingInfo] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [detectedLanguage, setDetectedLanguage] = useState(null);
  const [nameUpdateSuccess, setNameUpdateSuccess] = useState(null);
  //const [noteStyle, setNoteStyle] = useState('normal');
  const [sectionStyles, setSectionStyles] = useState({
    hpi: 'normal',
    ros: 'normal',
    assessment_plan: 'normal',
    additional_mdm: 'normal',
    physical_exam: 'normal',
    after_visit_summary: 'normal'
  });
  
  const [customSectionSendWith, setCustomSectionSendWith] = useState({});
  
  // Add state to track if defaults have been initialized
  const [defaultsInitialized, setDefaultsInitialized] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('en'); // Default to English
  const [selectedTemplate, setSelectedTemplate] = useState(null); // Template for note generation
  const [isFinishing, setIsFinishing] = useState(false);
  const [isUpdatingPronouns, setIsUpdatingPronouns] = useState(false);
  const [showFinishSuccess, setShowFinishSuccess] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editedPatientInfo, setEditedPatientInfo] = useState({
    first_name: '',
    last_name: '',
    middle_initial: '',
    dobMonth: '',
    dobDate: '',
    dobYear: '',
    legal_sex: '',
    email: '',
    phone: '',
    pronouns: '',
  });
  const [ehrErrorStatus, setEhrErrorStatus] = useState(null);
  const [patientSendingEHR, setPatientSendingEHR] = useState(null);
  const [customSections, setCustomSections] = useState([]);
  const [showAddSectionModal, setShowAddSectionModal] = useState(false);
  const [newSectionTitle, setNewSectionTitle] = useState('');
  const [newSectionPrompt, setNewSectionPrompt] = useState('');
  const [editedCombinedPatientsInfo, setEditedCombinedPatientsInfo] = useState([]);
  const [showCombinedNotification, setShowCombinedNotification] = useState(true);
  const [loadedPatientData, setLoadedPatientData] = useState([]);
  const [multiPatientDetection, setMultiPatientDetection] = useState(null);
  const [showMultiPatientConfirm, setShowMultiPatientConfirm] = useState(false);
  const [originalTranscription, setOriginalTranscription] = useState('');
  const [totalRecordingDuration, setTotalRecordingDuration] = useState(0);
  const [activeRecordingDuration, setActiveRecordingDuration] = useState(0);
  const [pendingRecordingDuration, setPendingRecordingDuration] = useState(0);
  const [hasInitialized, setHasInitialized] = useState(false);
  const prevPatientIdRef = useRef(null);
  const prevNoteSyncSignatureRef = useRef(null);
  const recordingStartedAtRef = useRef(null);
  
  // State for Blaine's Prompts toggle
  const [useBlainesPrompts, setUseBlainesPrompts] = useState(false);
  const [isSavingBlainesPrompts, setIsSavingBlainesPrompts] = useState(false);
  
  // Transcription modal states
  const [showTranscriptionModal, setShowTranscriptionModal] = useState(false);
  const [isConvertingTranscription, setIsConvertingTranscription] = useState(false);
  const [conversionProgress, setConversionProgress] = useState(0);
  
  // Determine if we're using SOAP format based on location
  const isSoapFormat = patient?.location === 'siliconbeach';
  const patientNoteSyncSignature = useMemo(() => {
    if (!patient) {
      return '';
    }

    const noteData = NOTE_SYNC_FIELDS.reduce((acc, field) => {
      acc[field] = patient[field] ?? null;
      return acc;
    }, {
      id: patient.id,
      has_soap_note: Boolean(patient.has_soap_note),
    });

    customSections.forEach(section => {
      const customKey = `custom_${section.id}`;
      noteData[customKey] = patient[customKey] ?? null;
    });

    return JSON.stringify(noteData);
  }, [patient, customSections]);
  const isEhrCooldown = Boolean(ehrCooldownUntil);
  const displayedRecordingDuration =
    totalRecordingDuration +
    pendingRecordingDuration +
    activeRecordingDuration;
  const shouldShowRecordingDuration = displayedRecordingDuration > 0;

  const showEhrCooldownNotice = (message) => {
    setEhrCooldownNotice(message);
    if (ehrCooldownNoticeTimeoutRef.current) {
      clearTimeout(ehrCooldownNoticeTimeoutRef.current);
    }
    ehrCooldownNoticeTimeoutRef.current = setTimeout(() => {
      setEhrCooldownNotice('');
    }, 2000);
  };

  const startEhrCooldown = () => {
    const until = Date.now() + EHR_COOLDOWN_MS;
    ehrCooldownUntilRef.current = until;
    setEhrCooldownUntil(until);
    if (ehrCooldownTimeoutRef.current) {
      clearTimeout(ehrCooldownTimeoutRef.current);
    }
    ehrCooldownTimeoutRef.current = setTimeout(() => {
      ehrCooldownUntilRef.current = null;
      setEhrCooldownUntil(null);
    }, EHR_COOLDOWN_MS);
  };

  useEffect(() => {
    return () => {
      if (ehrCooldownTimeoutRef.current) {
        clearTimeout(ehrCooldownTimeoutRef.current);
      }
      if (ehrCooldownNoticeTimeoutRef.current) {
        clearTimeout(ehrCooldownNoticeTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    ehrCooldownUntilRef.current = null;
    setEhrCooldownUntil(null);
    setEhrCooldownNotice('');
    if (ehrCooldownTimeoutRef.current) {
      clearTimeout(ehrCooldownTimeoutRef.current);
      ehrCooldownTimeoutRef.current = null;
    }
    if (ehrCooldownNoticeTimeoutRef.current) {
      clearTimeout(ehrCooldownNoticeTimeoutRef.current);
      ehrCooldownNoticeTimeoutRef.current = null;
    }
  }, [patient?.id]);

  useEffect(() => {
    let isActive = true;
    const controller = new AbortController();

    setShowTranscriptionModal(false);
    setOriginalTranscription('');
    setTotalRecordingDuration(0);
    setActiveRecordingDuration(0);
    setPendingRecordingDuration(0);
    recordingStartedAtRef.current = null;

    if (!patient?.id) {
      return () => controller.abort();
    }

    const loadTranscription = async () => {
      try {
        const response = await fetch(`/api/patients/${patient.id}/transcription`, {
          credentials: 'include',
          signal: controller.signal,
        });

        if (!response.ok) {
          if (response.status !== 404) {
            console.error('Failed to load transcription:', response.status);
          }
          return;
        }

        const data = await response.json();
        if (isActive) {
          setOriginalTranscription(data.transcription || '');
          setTotalRecordingDuration(Number(data.total_recording_duration) || 0);
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Error loading transcription:', err);
        }
      }
    };

    loadTranscription();

    return () => {
      isActive = false;
      controller.abort();
    };
  }, [patient?.id]);

  // Scroll to E/M Education section
  const scrollToEMEducation = () => {
    if (emEducationRef.current) {
      emEducationRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  // Scroll to E/M Education section for a specific patient in multi-patient view
  const scrollToEMEducationForPatient = (patientId) => {
    if (emEducationRefsMap.current[patientId]) {
      emEducationRefsMap.current[patientId].scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  // Update parent component about editing state
  useEffect(() => {
    onEditingStateChange(isEditingInfo);
  }, [isEditingInfo, onEditingStateChange]);

  useEffect(() => {
    if (patient && !isEditingInfo) {  // Only update if not currently editing
      const [firstName, ...rest] = patient.name.split(' ');
      const lastName = rest.length > 0 ? rest[rest.length - 1] : '';
      const middleInitial = rest.length > 1 ? rest[0] : '';
    
      let dobMonth = '', dobDate = '', dobYear = '';
      if (patient.dob) {
        // Handle both YYYY-MM-DD and MM/DD/YYYY formats
        if (patient.dob.includes('-')) {
          // Format is YYYY-MM-DD
          const [year, month, day] = patient.dob.split('-');
          dobMonth = month || '';
          dobDate = day || '';
          dobYear = year || '';
        } else {
          // Format is MM/DD/YYYY
          const [month, day, year] = patient.dob.split('/');
          dobMonth = month || '';
          dobDate = day || '';
          dobYear = year || '';
        }
      }
    
      setEditedPatientInfo({
        first_name: firstName || '',
        last_name: lastName || '',
        middle_initial: middleInitial || '',
        dobMonth: dobMonth,
        dobDate: dobDate,
        dobYear: dobYear,
        legal_sex: patient.legal_sex || '',
        birth_sex: patient.birth_sex || '',
        email: patient.email || '',
        phone: patient.phone || '',
        pronouns: patient.pronouns || '',
      });
    }
  }, [patient?.id, patient?.name, patient?.dob, patient?.legal_sex, patient?.birth_sex, patient?.email, patient?.phone, patient?.pronouns, isEditingInfo]);

  // Add this useEffect to initialize defaults based on location group
useEffect(() => {
  if (!defaultsInitialized) {
    const initializeDefaults = async () => {
      // Load Blaine's Prompts setting from userSettings
      if (userSettings.useBlainesPrompts !== undefined) {
        setUseBlainesPrompts(userSettings.useBlainesPrompts);
      }
      try {
        // First, try to load saved settings
        const response = await fetch('/api/user/settings', {
          credentials: 'include'
        });
        
        if (response.ok) {
          const settings = await response.json();
          if (settings.section_styles) {
            setSectionStyles(settings.section_styles);
          }
          if (settings.custom_section_send_with) {
            setCustomSectionSendWith(settings.custom_section_send_with);
          }
          setDefaultsInitialized(true);
          return;
        }
        
        // If no saved settings, check for Brenteview group
        const accountResponse = await fetch('/api/user/account', {
          credentials: 'include'
        });
        
        if (accountResponse.ok) {
          const account = await accountResponse.json();
          if (account.location_group === 'brenteview_group') {
            setSectionStyles({
              hpi: 'concise',
              ros: 'normal',
              assessment_plan: 'normal',
              physical_exam: 'normal'
            });
          }
        }
        
        setDefaultsInitialized(true);
      } catch (error) {
        console.error('Error initializing section styles:', error);
        setDefaultsInitialized(true);
      }

      // Load custom section send with settings from localStorage as fallback
      // (only if no settings were loaded from backend)
      if (Object.keys(customSectionSendWith).length === 0) {
        try {
          const savedSendWith = localStorage.getItem('customSectionSendWith');
          if (savedSendWith) {
            setCustomSectionSendWith(JSON.parse(savedSendWith));
          }
        } catch (error) {
          console.error('Error loading send with settings:', error);
        }
      }
    };
    
    initializeDefaults();
  }
}, [defaultsInitialized]);

// Sync useBlainesPrompts with userSettings prop changes
useEffect(() => {
  if (userSettings.useBlainesPrompts !== undefined) {
    setUseBlainesPrompts(userSettings.useBlainesPrompts);
  }
}, [userSettings.useBlainesPrompts]);

const handleSectionStyleChange = async (sectionKey, newStyle) => {
  const updatedStyles = {
    ...sectionStyles,
    [sectionKey]: newStyle
  };
  
  setSectionStyles(updatedStyles);
  
  // Save to backend
  try {
    await fetch('/api/user/settings', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        ...userSettings,
        section_styles: updatedStyles
      })
    });
  } catch (error) {
    console.error('Error saving section styles:', error);
  }
};

const handleSendWithChange = async (sectionKey, sendWithValue) => {
  console.log(`Updating send with for ${sectionKey} to ${sendWithValue}`);
  
  const updatedSendWith = {
    ...customSectionSendWith,
    [sectionKey]: sendWithValue
  };
  
  setCustomSectionSendWith(updatedSendWith);

  // Save to backend
  try {
    await fetch('/api/user/settings', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        ...userSettings,
        custom_section_send_with: updatedSendWith
      })
    });

    // Also save to localStorage as backup
    localStorage.setItem('customSectionSendWith', JSON.stringify(updatedSendWith));
  } catch (error) {
    console.error('Error saving send with settings:', error);
    // Keep localStorage as fallback if backend fails
    localStorage.setItem('customSectionSendWith', JSON.stringify(updatedSendWith));
  }
};

// Handle Blaine's Prompts toggle change
const handleBlainesPromptsToggle = async (checked) => {
  setUseBlainesPrompts(checked);
  setIsSavingBlainesPrompts(true);
  
  // Save to backend using the mutation
  try {
    if (saveSettingsMutation) {
      await saveSettingsMutation.mutateAsync({
        ...userSettings,
        useBlainesPrompts: checked
      });
    } else {
      // Fallback to direct API call if mutation not available
      await fetch('/api/user/settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...userSettings,
          useBlainesPrompts: checked
        }),
        credentials: 'include'
      });
    }
  } catch (error) {
    console.error('Error saving Blaine\'s Prompts setting:', error);
    // Revert the state if save failed
    setUseBlainesPrompts(!checked);
  } finally {
    setIsSavingBlainesPrompts(false);
  }
};

const handleCopyAllCombinedNotes = () => {
  try {
    const allNotes = loadedPatientData.map(patient => {
      const header = `===== PATIENT: ${formatPatientName(patient)} =====\n\n`;
      const isPatientSoap = patient?.location === 'siliconbeach';
      
      const visitSummary = patient.visit_summary 
        ? `VISIT SUMMARY:\n${Array.isArray(patient.visit_summary) 
            ? patient.visit_summary.join('\n') 
            : patient.visit_summary}\n\n` 
        : '';
      
      let sectionContent = '';
      
      if (isPatientSoap) {
        // SOAP format
        const subjective = patient.subjective 
          ? `SUBJECTIVE:\n${Array.isArray(patient.subjective) 
              ? patient.subjective.join('\n') 
              : patient.subjective}\n\n` 
          : '';
          
        const objective = patient.objective 
          ? `OBJECTIVE:\n${Array.isArray(patient.objective) 
              ? patient.objective.join('\n') 
              : patient.objective}\n\n` 
          : '';
          
        const assessment = patient.assessment 
          ? `ASSESSMENT:\n${Array.isArray(patient.assessment) 
              ? patient.assessment.join('\n') 
              : patient.assessment}\n\n` 
          : '';
          
        const plan = patient.plan 
          ? `PLAN:\n${Array.isArray(patient.plan) 
              ? patient.plan.join('\n') 
              : patient.plan}\n\n` 
          : '';
          
        sectionContent = subjective + objective + assessment + plan;
      } else {
        // Standard format
        const hpi = patient.hpi 
          ? `HISTORY OF PRESENT ILLNESS:\n${Array.isArray(patient.hpi) 
              ? patient.hpi.join('\n') 
              : patient.hpi}\n\n` 
          : '';
          
        const ros = patient.ros 
          ? `REVIEW OF SYMPTOMS:\n${Array.isArray(patient.ros) 
              ? patient.ros.join('\n') 
              : patient.ros}\n\n` 
          : '';
          
        const assessmentPlan = patient.assessment_plan 
          ? `ASSESSMENT & PLAN:\n${Array.isArray(patient.assessment_plan) 
              ? patient.assessment_plan.join('\n') 
              : patient.assessment_plan}\n\n` 
          : '';
          
        sectionContent = hpi + ros + assessmentPlan;
      }

      const physicalExam = patient.physical_exam && userSettings.showPhysicalExam
        ? `PHYSICAL EXAM:\n${Array.isArray(patient.physical_exam)
            ? patient.physical_exam.join('\n')
            : patient.physical_exam}\n\n`
        : '';

      const additionalMDM = patient.additional_mdm && userSettings.showAdditionalMDM
        ? `ADDITIONAL MDM:\n${Array.isArray(patient.additional_mdm)
            ? patient.additional_mdm.join('\n')
            : patient.additional_mdm}\n\n`
        : '';

      const afterVisitSummary = patient.after_visit_summary && patient.after_visit_summary.length > 0
        ? `Patient Instructions:\n${Array.isArray(patient.after_visit_summary)
          ? patient.after_visit_summary.join('\n')
          : patient.after_visit_summary}\n\n`
        : '';

      return header + visitSummary + sectionContent + physicalExam + additionalMDM + afterVisitSummary;
    }).join('\n\n' + '='.repeat(40) + '\n\n');
    
    // Remove markdown formatting for copying
    const cleanNotes = allNotes.replace(/\*\*(.*?)\*\*/g, '$1');
    navigator.clipboard.writeText(cleanNotes);
    setNameUpdateSuccess("All patient notes copied to clipboard");
    setTimeout(() => setNameUpdateSuccess(null), 3000);
  } catch (error) {
    console.error('Error copying notes:', error);
    setError('Failed to copy notes to clipboard');
  }
};

const handleCopyIndividualPatientNotes = (patient) => {
  try {
    const header = `===== PATIENT: ${formatPatientName(patient)} =====\n\n`;
    const isPatientSoap = patient?.location === 'siliconbeach';
    
    const visitSummary = patient.visit_summary 
      ? `VISIT SUMMARY:\n${Array.isArray(patient.visit_summary) 
          ? patient.visit_summary.join('\n') 
          : patient.visit_summary}\n\n` 
      : '';
    
    let sectionContent = '';
    
    if (isPatientSoap) {
      // SOAP format
      const subjective = patient.subjective 
        ? `SUBJECTIVE:\n${Array.isArray(patient.subjective) 
            ? patient.subjective.join('\n') 
            : patient.subjective}\n\n` 
        : '';
        
      const objective = patient.objective 
        ? `OBJECTIVE:\n${Array.isArray(patient.objective) 
            ? patient.objective.join('\n') 
            : patient.objective}\n\n` 
        : '';
        
      const assessment = patient.assessment 
        ? `ASSESSMENT:\n${Array.isArray(patient.assessment) 
            ? patient.assessment.join('\n') 
            : patient.assessment}\n\n` 
        : '';
        
      const plan = patient.plan 
        ? `PLAN:\n${Array.isArray(patient.plan) 
            ? patient.plan.join('\n') 
            : patient.plan}\n\n` 
        : '';
        
      sectionContent = subjective + objective + assessment + plan;
    } else {
      // Standard format
      const hpi = patient.hpi 
        ? `HISTORY OF PRESENT ILLNESS:\n${Array.isArray(patient.hpi) 
            ? patient.hpi.join('\n') 
            : patient.hpi}\n\n` 
        : '';
        
      const ros = patient.ros 
        ? `REVIEW OF SYMPTOMS:\n${Array.isArray(patient.ros) 
            ? patient.ros.join('\n') 
            : patient.ros}\n\n` 
        : '';
        
      const assessmentPlan = patient.assessment_plan 
        ? `ASSESSMENT & PLAN:\n${Array.isArray(patient.assessment_plan) 
            ? patient.assessment_plan.join('\n') 
            : patient.assessment_plan}\n\n` 
        : '';
        
      sectionContent = hpi + ros + assessmentPlan;
    }

      const physicalExam = patient.physical_exam && userSettings.showPhysicalExam
        ? `PHYSICAL EXAM:\n${Array.isArray(patient.physical_exam)
            ? patient.physical_exam.join('\n')
            : patient.physical_exam}\n\n`
        : '';

    const additionalMDM = patient.additional_mdm && userSettings.showAdditionalMDM
      ? `ADDITIONAL MDM:\n${Array.isArray(patient.additional_mdm)
          ? patient.additional_mdm.join('\n')
          : patient.additional_mdm}\n\n`
      : '';

    const afterVisitSummary = patient.after_visit_summary && patient.after_visit_summary.length > 0
      ? `Patient Instructions:\n${Array.isArray(patient.after_visit_summary)
        ? patient.after_visit_summary.join('\n')
        : patient.after_visit_summary}\n\n`
      : '';

    const patientNotes = header + visitSummary + sectionContent + physicalExam + additionalMDM + afterVisitSummary;
    // Remove markdown formatting for copying
    const cleanNotes = patientNotes.replace(/\*\*(.*?)\*\*/g, '$1');
    navigator.clipboard.writeText(cleanNotes);
    setNameUpdateSuccess(`Notes for ${formatPatientName(patient)} copied to clipboard`);
    setTimeout(() => setNameUpdateSuccess(null), 3000);
  } catch (error) {
    console.error('Error copying notes:', error);
    setError('Failed to copy notes to clipboard');
  }
};

const handleCombinedPatientUpdate = async (patientId, sectionTitle, newData) => {
  const standardSectionMap = {
    'Visit Summary': 'visit_summary',
    'History of Present Illness': 'hpi',
    'Review of Symptoms': 'ros',
    'Physical Exam': 'physical_exam',
    'Assessment & Plan': 'assessment_plan',
    'Suggested Tests': 'suggested_tests',
    'ICD-10 Codes': 'icd_codes',
    'CPT Codes': 'cpt_codes',
    'EM Codes': 'em_codes',
    'EM Education': 'em_education',
    'Patient Instructions': 'after_visit_summary',
    'Additional MDM': 'additional_mdm',
  };
  
  const soapSectionMap = {
    'Visit Summary': 'visit_summary',
    'Subjective': 'subjective',
    'Objective': 'objective',
    'Assessment': 'assessment',
    'Plan': 'plan',
    'Physical Exam': 'physical_exam',
    'Suggested Tests': 'suggested_tests',
    'ICD-10 Codes': 'icd_codes',
    'EM Codes': 'em_codes',
    'CPT Codes': 'cpt_codes',
    'Patient Instructions': 'after_visit_summary',
    'EM Education': 'em_education'
  };
  
  const patientToUpdate = loadedPatientData.find(p => p.id === patientId);
  const isPatientSoap = patientToUpdate?.location === 'siliconbeach';
  const sectionMap = isPatientSoap ? soapSectionMap : standardSectionMap;
  const sectionKey = sectionMap[sectionTitle];
  if (!sectionKey) return;
  
  const patientIndex = loadedPatientData.findIndex(p => p.id === patientId);
  if (patientIndex === -1) return;
  
  const updatedData = {
    visit_summary: patientToUpdate.visit_summary || [],
    // Standard format fields
    hpi: patientToUpdate.hpi || [],
    assessment_plan: patientToUpdate.assessment_plan || [],
    ros: patientToUpdate.ros || [],
    // SOAP format fields
    subjective: patientToUpdate.subjective || [],
    objective: patientToUpdate.objective || [],
    assessment: patientToUpdate.assessment || [],
    plan: patientToUpdate.plan || [],
    // Common fields
    suggested_tests: patientToUpdate.suggested_tests || [],
    icd_codes: patientToUpdate.icd_codes || [],
    em_codes: patientToUpdate.em_codes || [],
    em_education: patientToUpdate.em_education || [],
    cpt_codes: patientToUpdate.cpt_codes || [],
    physical_exam: patientToUpdate.physical_exam || [],
    after_visit_summary: patientToUpdate.after_visit_summary || []
  };
  
  updatedData[sectionKey] = newData;
  
  const newPatientData = [...loadedPatientData];
  newPatientData[patientIndex] = {
    ...patientToUpdate,
    ...updatedData
  };
  setLoadedPatientData(newPatientData);
  
  try {
    const response = await fetch(`/api/patients/${patientId}/soap`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(updatedData),
    });
  
    if (!response.ok) {
      throw new Error('Failed to save changes');
    }
  
    const updatedPatient = await response.json();
    const updatedPatientData = loadedPatientData.map(p => 
      p.id === patientId ? updatedPatient : p
    );
    setLoadedPatientData(updatedPatientData);
    setError(null);
  } catch (err) {
    console.error('Error:', err);
    setError('Failed to save changes');
  }
};

const handleCombinedPronounsChange = async (patientId, newPronouns) => {
  try {
    const patientIndex = loadedPatientData.findIndex(p => p.id === patientId);
    if (patientIndex === -1) return;
    
    const patientToUpdate = loadedPatientData[patientIndex];
    setIsUpdatingPronouns(true);
    setError(null);
    
    const response = await fetch(`/api/patients/${patientId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        pronouns: newPronouns
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to update pronouns');
    }

    if (patientToUpdate.hpi?.length || patientToUpdate.assessment_plan?.length || patientToUpdate.ros?.length) {
      const updatedNotes = {
        visit_summary: patientToUpdate.visit_summary || [],
        hpi: patientToUpdate.hpi || [],
        assessment_plan: patientToUpdate.assessment_plan || [],
        ros: patientToUpdate.ros || [],
        physical_exam: patientToUpdate.physical_exam || [],
        suggested_tests: patientToUpdate.suggested_tests || [],
        icd_codes: patientToUpdate.icd_codes || [],
        em_codes: patientToUpdate.em_codes || [],
        em_education: patientToUpdate.em_education || [],
        cpt_codes: patientToUpdate.cpt_codes || [],
        after_visit_summary: patientToUpdate.after_visit_summary || []
      };
      
      ['hpi', 'assessment_plan', 'ros', 'physical_exam'].forEach(field => {
        if (Array.isArray(updatedNotes[field])) {
          updatedNotes[field] = updatedNotes[field].map(item => 
            applyPronouns(item, newPronouns)
          );
        }
      });
      
      if (Array.isArray(updatedNotes.visit_summary)) {
        updatedNotes.visit_summary = updatedNotes.visit_summary.map(item => 
          applyPronouns(item, newPronouns)
        );
      } else if (typeof updatedNotes.visit_summary === 'string') {
        updatedNotes.visit_summary = applyPronouns(updatedNotes.visit_summary, newPronouns);
      }
      
      const notesResponse = await fetch(`/api/patients/${patientId}/soap`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(updatedNotes),
      });
      
      if (notesResponse.ok) {
        const updatedPatientWithNotes = await notesResponse.json();
        const newPatientData = [...loadedPatientData];
        newPatientData[patientIndex] = updatedPatientWithNotes;
        setLoadedPatientData(newPatientData);
      }
    }
    
    setNameUpdateSuccess(`Pronouns updated to: ${newPronouns} for ${formatPatientName(patientToUpdate)}`);
    setTimeout(() => setNameUpdateSuccess(null), 3000);
  } catch (err) {
    console.error('Error updating pronouns:', err);
    setError('Failed to update pronouns');
  } finally {
    setIsUpdatingPronouns(false);
  }
};
  
  // Recording / transcription states
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorder = useRef(null);
  const audioContext = useRef(null);
  const audioProcessor = useRef(null);
  const audioStream = useRef(null);
  const isRecordingRef = useRef(false);  // Ref to track recording state for audio processor
  const isStoppingRecordingRef = useRef(false);
  const [transcriptProgress, setTranscriptProgress] = useState(''); // partial transcripts
  const [finalTranscript, setFinalTranscript] = useState('');       // accumulate final transcripts
  const finalTranscriptRef = useRef('');   // Source of truth for stop-time processing
  const lastFinalTranscriptAtRef = useRef(null);
  const latestPartialTranscriptRef = useRef('');
  const lastPartialTranscriptAtRef = useRef(null);
  const webSocket = useRef(null);

  // WebSocket URL
  const wsUrl = 'wss://demo.prefacehealth.com/api/transcribe';
  //const wsUrl = 'ws://localhost:8001/transcribe';

  const flushPendingTranscript = async () => {
    const maxWaitMs = 3500;
    const pollIntervalMs = 100;
    const stableWindowMs = 400;
    const start = Date.now();

    while (Date.now() - start < maxWaitMs) {
      const finalTranscriptValue = finalTranscriptRef.current.trim();
      const partialTranscriptValue = latestPartialTranscriptRef.current.trim();
      const lastFinalAt = lastFinalTranscriptAtRef.current;
      const lastPartialAt = lastPartialTranscriptAtRef.current;
      const stableForMs = lastFinalAt ? Date.now() - lastFinalAt : Number.POSITIVE_INFINITY;
      const partialStableForMs = lastPartialAt ? Date.now() - lastPartialAt : Number.POSITIVE_INFINITY;

      if (finalTranscriptValue && stableForMs >= stableWindowMs) {
        return { transcript: finalTranscriptValue, source: 'final' };
      }

      // Fallback when STT never emits a final segment before stream close.
      if (!finalTranscriptValue && partialTranscriptValue && partialStableForMs >= stableWindowMs) {
        return { transcript: partialTranscriptValue, source: 'partial' };
      }

      await new Promise(resolve => setTimeout(resolve, pollIntervalMs));
    }

    const finalTranscriptValue = finalTranscriptRef.current.trim();
    if (finalTranscriptValue) {
      return { transcript: finalTranscriptValue, source: 'final' };
    }

    return { transcript: latestPartialTranscriptRef.current.trim(), source: 'partial' };
  };

  const stopMediaRecorderAndFlush = async () => {
    if (!mediaRecorder.current || mediaRecorder.current.state === 'inactive') {
      return;
    }

    const recorder = mediaRecorder.current;

    await new Promise((resolve) => {
      let settled = false;
      const settle = () => {
        if (settled) {
          return;
        }
        settled = true;
        resolve();
      };

      const originalOnStop = recorder.onstop;
      const originalOnError = recorder.onerror;

      recorder.onstop = (event) => {
        if (typeof originalOnStop === 'function') {
          try {
            originalOnStop(event);
          } catch (handlerError) {
            console.error('Error in original MediaRecorder onstop handler:', handlerError);
          }
        }
        // Give ondataavailable a small window to fire/send final blob.
        setTimeout(settle, 150);
      };

      recorder.onerror = (event) => {
        if (typeof originalOnError === 'function') {
          try {
            originalOnError(event);
          } catch (handlerError) {
            console.error('Error in original MediaRecorder onerror handler:', handlerError);
          }
        }
        settle();
      };

      try {
        if (recorder.state === 'recording') {
          recorder.requestData();
        }
        recorder.stop();
      } catch (stopError) {
        console.error('Error stopping MediaRecorder:', stopError);
        settle();
      }

      setTimeout(settle, 1200);
    });

    if (recorder.stream) {
      recorder.stream.getTracks().forEach(track => track.stop());
    }
  };

  useEffect(() => {
    if (!isRecording || !recordingStartedAtRef.current) {
      setActiveRecordingDuration(0);
      return undefined;
    }

    const updateRecordingDuration = () => {
      const elapsedSeconds = Math.max(
        0,
        Math.floor((Date.now() - recordingStartedAtRef.current) / 1000)
      );
      setActiveRecordingDuration(elapsedSeconds);
    };

    updateRecordingDuration();
    const intervalId = setInterval(updateRecordingDuration, 1000);

    return () => clearInterval(intervalId);
  }, [isRecording]);

  useEffect(() => {
    // If SOAP notes have changed in the backend (e.g. newly created),
    // you might want to animate or refresh the UI here:
    if (patient && prevPatientRef.current &&
        patient.id === prevPatientRef.current.id &&
        patient.has_soap_note !== prevPatientRef.current.has_soap_note) {
      const container = document.querySelector('.soap-notes-container');
      if (container) {
        container.classList.add('fade-in');
        setTimeout(() => container.classList.remove('fade-in'), 500);
      }
    }
    prevPatientRef.current = patient;
  }, [patient?.id, patient?.has_soap_note]);

  useEffect(() => {
    const patientChanged = prevPatientIdRef.current !== patient?.id;
    prevPatientIdRef.current = patient?.id;
    const noteChanged = prevNoteSyncSignatureRef.current !== patientNoteSyncSignature;
    
    // Rehydrate when the selected patient changes, on first load, or when a poll
    // returns new note contents for the same patient. Delay external note sync
    // while a section is actively being edited so in-progress text is not replaced.
    if (!hasInitialized || patientChanged || (noteChanged && !activeSection)) {
      setEditedData(buildEditedDataFromPatient(patient, customSections));
      setHasInitialized(true);
      prevNoteSyncSignatureRef.current = patientNoteSyncSignature;
    } else {
      // Just update custom sections that don't exist in editedData yet
      const updatedData = { ...editedData };
      let hasNewSections = false;
      
      customSections.forEach(section => {
        const customKey = `custom_${section.id}`;
        if (!(customKey in updatedData)) {
          updatedData[customKey] = patient?.[customKey] || [''];
          hasNewSections = true;
        }
      });
      
      if (hasNewSections) {
        setEditedData(updatedData);
      }
    }
  }, [patient?.id, patient?.has_soap_note, patientNoteSyncSignature, isSoapFormat, customSections.length, activeSection]);

  // Reset active section when patient ID changes
  useEffect(() => {
    setActiveSection(null);
  }, [patient?.id]);

  // Close editing if user clicks outside the container
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setActiveSection(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clean up MediaRecorder
      if (mediaRecorder.current && mediaRecorder.current.state !== 'inactive') {
        mediaRecorder.current.stop();
        mediaRecorder.current.stream.getTracks().forEach(track => track.stop());
      }
      // Clean up Web Audio API
      if (audioProcessor.current) {
        audioProcessor.current.disconnect();
      }
      if (audioContext.current) {
        audioContext.current.close();
      }
      if (audioStream.current) {
        audioStream.current.getTracks().forEach(track => track.stop());
      }
      // Clean up WebSocket
      if (webSocket.current) {
        webSocket.current.close();
      }
    };
  }, []);

  // Send language setting when recording starts
  useEffect(() => {
    if (isRecording && webSocket.current?.readyState === WebSocket.OPEN) {
      webSocket.current.send(JSON.stringify({
        command: "set_language",
        language: selectedLanguage
      }));
    }
  }, [selectedLanguage, isRecording]);

  // Load custom sections on component mount
  useEffect(() => {
    const loadCustomSections = async () => {
      try {
        const response = await fetch('/api/custom-sections', {
          credentials: 'include',
        });

        if (response.ok) {
          const sections = await response.json();
          setCustomSections(sections);
        }
      } catch (err) {
        console.error('Error loading custom sections:', err);
      }
    };

    loadCustomSections();
  }, []);

  // Load combined patient data
  useEffect(() => {
    const fetchCombinedPatientsData = async () => {
      if (patient?.is_combined && patient?.combined_patients?.length > 0) {
        const patientDataPromises = patient.combined_patients.map(async (pid) => {
          const response = await fetch(`/api/patients/${pid}`, {
            credentials: 'include'
          });
          
          if (response.ok) {
            return response.json();
          }
          return null;
        });
        
        const patientsData = await Promise.all(patientDataPromises);
        setLoadedPatientData(patientsData.filter(p => p !== null));
      } else {
        setLoadedPatientData([]);
      }
    };
    
    if (patient?.is_combined) {
      fetchCombinedPatientsData();
    }
  }, [patient?.id, patient?.is_combined]);

  // Check for multi-patient detection dialog
  useEffect(() => {
    if (showMultiPatientConfirm) {
      console.log("🔍 Multi-patient dialog should be visible");
      console.log("Detection info:", multiPatientDetection);
    }
  }, [showMultiPatientConfirm, multiPatientDetection]);

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      const response = await fetch(`/api/patients/${patient.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to delete patient');
      }

      setIsDeleteDialogOpen(false);
      onDeletePatient(patient.id);
    } catch (err) {
      console.error('Error deleting patient:', err);
      setError('Failed to delete patient');
      
      // Clear the error message after 5 seconds
      setTimeout(() => {
        setError(null);
      }, 5000);
    } finally {
      setIsDeleting(false);
    }
  };

  const applyPronouns = (text, pronouns) => {
    if (!text || !pronouns) return text;
    
    // Define pronoun sets and their various forms
    const pronounSets = {
      'He/him': {
        subject: 'he',
        object: 'him',
        possessive: 'his',
        possessiveAdj: 'his',
        reflexive: 'himself',
        // Map for singular verb forms (he/she)
        singularVerbs: true
      },
      'She/her': {
        subject: 'she',
        object: 'her',
        possessive: 'hers',
        possessiveAdj: 'her',
        reflexive: 'herself',
        singularVerbs: true
      },
      'They/them': {
        subject: 'they',
        object: 'them',
        possessive: 'theirs',
        possessiveAdj: 'their',
        reflexive: 'themselves',
        singularVerbs: false
      }
    };
    
    // Get the pronoun set
    const pronounSet = pronounSets[pronouns];
    if (!pronounSet) return text;
    
    // First, break the text into sentences for proper capitalization handling
    const sentences = text.split(/(?<=[.!?])\s+/);
    
    let processedText = sentences.map(sentence => {
      // First handle "The patient" or "the patient" replacements
      let processedSentence = sentence
        // Replace "The patient's" or "the patient's" with possessive adjective
        .replace(/\b(The|the) patient's\b/g, (match, theWord) => {
          return pronounSet.possessiveAdj;
        })
        
        // Replace "The patient" or "the patient" with subject pronoun
        .replace(/\b(The|the) patient\b/g, (match, theWord) => {
          return pronounSet.subject;
        })
        
        // Replace standalone "patient" with object form
        .replace(/\b(patient)\b(?!\s*'s)/g, pronounSet.object)
        
        // Replace other pronouns for consistency
        .replace(/\b(he|she|they)\b/gi, pronounSet.subject)
        .replace(/\b(his|her|their)\b/gi, pronounSet.possessiveAdj)
        .replace(/\b(him|her|them)\b/gi, pronounSet.object);
  
      // Fix "the he/she/they" mistakes
      processedSentence = processedSentence.replace(/\b(the|The) (he|she|they)\b/gi, "$2");
      
      // Verb conjugation fixes for singular vs plural
      if (pronounSet.singularVerbs) {
        // For singular pronouns (he/she), ensure verbs have correct conjugation
        processedSentence = processedSentence
          // Fix common singular verb patterns
          .replace(new RegExp(`\\b${pronounSet.subject}\\s+report\\b`, 'gi'), `${pronounSet.subject} reports`)
          .replace(new RegExp(`\\b${pronounSet.subject}\\s+have\\b`, 'gi'), `${pronounSet.subject} has`)
          .replace(new RegExp(`\\b${pronounSet.subject}\\s+do\\b`, 'gi'), `${pronounSet.subject} does`)
          .replace(new RegExp(`\\b${pronounSet.subject}\\s+are\\b`, 'gi'), `${pronounSet.subject} is`)
          .replace(new RegExp(`\\b${pronounSet.subject}\\s+were\\b`, 'gi'), `${pronounSet.subject} was`);
      } else {
        // For plural pronouns (they), ensure verbs have correct conjugation
        processedSentence = processedSentence
          // Fix common plural verb patterns
          .replace(new RegExp(`\\b${pronounSet.subject}\\s+reports\\b`, 'gi'), `${pronounSet.subject} report`)
          .replace(new RegExp(`\\b${pronounSet.subject}\\s+has\\b`, 'gi'), `${pronounSet.subject} have`)
          .replace(new RegExp(`\\b${pronounSet.subject}\\s+does\\b`, 'gi'), `${pronounSet.subject} do`)
          .replace(new RegExp(`\\b${pronounSet.subject}\\s+is\\b`, 'gi'), `${pronounSet.subject} are`)
          .replace(new RegExp(`\\b${pronounSet.subject}\\s+was\\b`, 'gi'), `${pronounSet.subject} were`);
      }
      
      // Capitalize first word of sentence
      return processedSentence.replace(/^(\w)/, match => match.toUpperCase());
    });
    
    // Join sentences back together
    return processedText.join(' ');
  };

  // Function to convert transcription into notes
  const handleConvertTranscription = async () => {
    if (!originalTranscription) {
      alert('No transcription available to convert');
      return;
    }
    
    setIsConvertingTranscription(true);
    setConversionProgress(0);
    
    try {
      // Simulate progress as we process
      const progressSteps = [10, 30, 50, 70, 90, 100];
      let currentStep = 0;
      
      // Create progress update function
      const updateProgress = () => {
        if (currentStep < progressSteps.length) {
          setConversionProgress(progressSteps[currentStep]);
          currentStep++;
          if (currentStep < progressSteps.length) {
            setTimeout(updateProgress, 500); // Update every 500ms
          }
        }
      };
      
      updateProgress();
      
      // Create request payload similar to process_transcription
      const requestData = {
        transcription: originalTranscription,
        patient_id: patient.id,
        location: currentLocation || patient.location,
        userSettings: userSettings,
        user_id: patient.user_id || 'system',
        source: 'manual_conversion',
        use_formatted: true,
        section_styles: sectionStyles,
        skip_detection: true // Skip multi-patient detection for manual conversion
      };
      
      const response = await fetch('/process_transcription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.error) {
        throw new Error(result.error);
      }
      
      // Update the patient data with the converted notes
      if (result.success && result.patient) {
        setEditedData({
          visit_summary: result.patient.visit_summary || [],
          hpi: result.patient.hpi || '',
          assessment_plan: result.patient.assessment_plan || '',
          ros: result.patient.ros || '',
          subjective: result.patient.subjective || '',
          objective: result.patient.objective || '',
          assessment: result.patient.assessment || '',
          plan: result.patient.plan || '',
          suggested_tests: result.patient.suggested_tests || [],
          icd_codes: result.patient.icd_codes || [],
          em_codes: result.patient.em_codes || [],
          cpt_codes: result.patient.cpt_codes || [],
          physical_exam: result.patient.physical_exam || [],
          additional_mdm: result.patient.additional_mdm || [],
          after_visit_summary: result.patient.after_visit_summary || [],
          em_education: result.patient.em_education || [],
          // MA Section fields
          ma_medications: result.patient.ma_medications || [],
          ma_inhouse_labs: result.patient.ma_inhouse_labs || [],
          ma_sendout_labs: result.patient.ma_sendout_labs || [],
          ma_procedures: result.patient.ma_procedures || [],
        });
        
        // Close the modal
        setShowTranscriptionModal(false);
        
        // Show success message
        alert('Transcription successfully converted to notes!');
        
        // If there's an onPatientUpdate callback, call it with the updated patient
        if (onPatientUpdate) {
          onPatientUpdate(result.patient);
        }
      } else {
        throw new Error('Failed to process transcription');
      }
      
    } catch (error) {
      console.error('Error converting transcription:', error);
      alert('Failed to convert transcription: ' + error.message);
    } finally {
      setIsConvertingTranscription(false);
      setConversionProgress(0);
    }
  };

  const buildUpdatedData = (overrides = {}) => {
    const updatedData = {
      visit_summary: editedData.visit_summary || [],
      // Standard format fields
      hpi: editedData.hpi || [],
      assessment_plan: editedData.assessment_plan || [],
      ros: editedData.ros || [],
      // SOAP format fields
      subjective: editedData.subjective || [],
      objective: editedData.objective || [],
      assessment: editedData.assessment || [],
      plan: editedData.plan || [],
      // Common fields
      suggested_tests: editedData.suggested_tests || [],
      icd_codes: editedData.icd_codes || [],
      cpt_codes: editedData.cpt_codes || [],
      em_codes: editedData.em_codes || [],
      physical_exam: editedData.physical_exam || [],
      after_visit_summary: editedData.after_visit_summary || [],
      em_education: editedData.em_education || [],
      additional_mdm: editedData.additional_mdm || [],
      mdm_calculator: editedData.mdm_calculator || [],
      // MA Section fields
      ma_medications: editedData.ma_medications || [],
      ma_inhouse_labs: editedData.ma_inhouse_labs || [],
      ma_sendout_labs: editedData.ma_sendout_labs || [],
      ma_procedures: editedData.ma_procedures || [],
    };

    // Include all custom sections in the update
    customSections.forEach(section => {
      const customKey = `custom_${section.id}`;
      updatedData[customKey] = editedData[customKey] || [''];
    });

    return { ...updatedData, ...overrides };
  };

  const saveUpdatedData = async (updatedData) => {
    // Update local state immediately
    setEditedData(updatedData);

    try {
      const response = await fetch(`/api/patients/${patient.id}/soap`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(updatedData),
      });

      if (!response.ok) {
        throw new Error('Failed to save changes');
      }

      const updatedPatient = await response.json();
      onPatientUpdate(updatedPatient);
      setError(null);
    } catch (err) {
      console.error('Error:', err);
      setError('Failed to save changes');
    }
  };

  // PATCH to update one SOAP section at a time
  const handleUpdate = async (section, newData) => {
    // Map section titles to field names based on format
    const standardSectionMap = {
      'Visit Summary': 'visit_summary',
      'History of Present Illness': 'hpi',
      'Review of Symptoms': 'ros',
      'Assessment & Plan': 'assessment_plan',
      'Physical Exam': 'physical_exam',
      'Suggested Tests': 'suggested_tests',
      'ICD-10 Codes': 'icd_codes',
      'EM Codes': 'em_codes',
      'CPT Codes': 'cpt_codes',
      'Patient Instructions': 'after_visit_summary',
      'EM Education': 'em_education',
      'Additional MDM': 'additional_mdm',
    };
    
    const soapSectionMap = {
      'Visit Summary': 'visit_summary',
      'Subjective': 'subjective',
      'Objective': 'objective',
      'Assessment': 'assessment',
      'Plan': 'plan',
      'Physical Exam': 'physical_exam',
      'Suggested Tests': 'suggested_tests',
      'ICD-10 Codes': 'icd_codes',
      'EM Codes': 'em_codes',
      'CPT Codes': 'cpt_codes',
      'Patient Instructions': 'after_visit_summary',
      'EM Education': 'em_education',
    };
    
    // Choose the appropriate section map based on format
    const sectionMap = isSoapFormat ? soapSectionMap : standardSectionMap;
    let sectionKey = sectionMap[section];
    
    if (!sectionKey) {
      // Look for custom section
      const customSection = customSections.find(cs => cs.title === section);
      if (customSection) {
        sectionKey = `custom_${customSection.id}`;
      } else {
        return; // Unknown section
      }
    }

    const updatedData = buildUpdatedData({ [sectionKey]: newData });
    await saveUpdatedData(updatedData);
  };

  const handleMASectionUpdate = async (maUpdates) => {
    const updatedData = buildUpdatedData(maUpdates);
    await saveUpdatedData(updatedData);
  };

  // Custom sections handlers
const handleAddSection = async () => {
  if (!newSectionTitle.trim() || !newSectionPrompt.trim()) {
    return;
  }

  try {
    const response = await fetch('/api/custom-sections', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        title: newSectionTitle.trim(),
        prompt: newSectionPrompt.trim()
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to save custom section');
    }

    const newSection = await response.json();
    setCustomSections(prev => [...prev, newSection]);
    setNewSectionTitle('');
    setNewSectionPrompt('');
    setShowAddSectionModal(false);
  } catch (err) {
    console.error('Error saving custom section:', err);
    setError('Failed to save custom section');
  }
};

const handleDeleteCustomSection = async (sectionId) => {
  try {
    const response = await fetch(`/api/custom-sections/${sectionId}`, {
      method: 'DELETE',
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Failed to delete custom section');
    }

    setCustomSections(prev => prev.filter(section => section.id !== sectionId));
  } catch (err) {
    console.error('Error deleting custom section:', err);
    setError('Failed to delete custom section');
  }
};

// Multi-patient handlers
const handleConfirmMultiPatients = async () => {
  setIsTranscribing(true);
  
  try {
    console.log("Sending confirmation for multiple patients");
    console.log("Original transcription:", originalTranscription);
    console.log("Detection info:", multiPatientDetection);
    console.log("Current patient location:", patient.location);
    
    const response = await fetch('/api/process-transcription', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        transcription: originalTranscription,
        confirmed_multiple_patients: true,
        detection_info: multiPatientDetection,
        recording_duration: pendingRecordingDuration,
        userSettings: { ...userSettings, useBlainesPrompts: useBlainesPrompts },
        section_styles: sectionStyles,
        detected_language: selectedLanguage,
        location: currentLocation || patient.location,
        patient_id: patient.id,
        previous_note: editedData,
        create_individual_patients_only: true,
        selected_template_id: selectedTemplate?._id || null
      })
    });
    
    const result = await response.json();
    console.log("Multi-patient processing result:", result);
  
    if (result.success) {
      setTotalRecordingDuration(prev => prev + pendingRecordingDuration);
      setPendingRecordingDuration(0);
      const count = result.processed_patients?.length || 0;
      setNameUpdateSuccess(`${count} patient${count !== 1 ? 's' : ''} created successfully!`);
      setTimeout(() => setNameUpdateSuccess(null), 3000);
      
      // Delete the original patient from the list
      if (typeof onDeletePatient === 'function') {
        onDeletePatient(patient.id);
      }
      
      // Refresh the patient list to show the new individual patients
      if (typeof onPatientListUpdated === 'function') {
        onPatientListUpdated();
      }
    }
  } catch (error) {
    console.error('Error processing multiple patients:', error);
    setError(`Failed to process multiple patients: ${error.message}`);
  } finally {
    setIsTranscribing(false);
    setShowMultiPatientConfirm(false);
  }
};

const handleDeclineMultiPatients = async () => {
  setIsTranscribing(true);
  
  try {
    const response = await fetch('/api/process-transcription', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        transcription: originalTranscription,
        previous_note: editedData,
        patient_id: patient.id,
        recording_duration: pendingRecordingDuration,
        location: currentLocation || patient.location,
        userSettings: { ...userSettings, useBlainesPrompts: useBlainesPrompts },
        section_styles: sectionStyles,
        detected_language: selectedLanguage,
        skip_detection: true,
        selected_template_id: selectedTemplate?._id || null
      })
    });
    
    if (!response.ok) {
      throw new Error('Processing failed');
    }
    
    const processedData = await response.json();
    setTotalRecordingDuration(prev => prev + pendingRecordingDuration);
    setPendingRecordingDuration(0);
    setEditedData(processedData);
    onPatientUpdate(processedData);
    
    if (processedData.name_updated && processedData.extracted_name) {
      setNameUpdateSuccess(`Patient name updated to: ${processedData.extracted_name}`);
      setTimeout(() => setNameUpdateSuccess(null), 3000);
    }
  } catch (error) {
    console.error('Error processing single patient:', error);
    setError(`Failed to process transcription: ${error.message}`);
  } finally {
    setIsTranscribing(false);
  }
};

const handleCombinedTranscriptionProcessing = async (transcription) => {
  setIsTranscribing(true);
  
  try {
    const processPromises = loadedPatientData.map(async (individualPatient) => {
      const processResponse = await fetch('/api/process-transcription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          transcription: transcription,
          previous_note: {
            visit_summary: individualPatient.visit_summary || [],
            hpi: individualPatient.hpi || [],
            assessment_plan: individualPatient.assessment_plan || [],
            ros: individualPatient.ros || [],
            after_visit_summary: individualPatient.after_visit_summary || [],
            additional_mdm: individualPatient.additional_mdm || [],
          },
          patient_id: individualPatient.id,
          userSettings: { ...userSettings, useBlainesPrompts: useBlainesPrompts },
          section_styles: sectionStyles,
          detected_language: selectedLanguage,
          skip_detection: true,
          location: currentLocation || patient.location,
          selected_template_id: selectedTemplate?._id || null
        })
      });
      
      if (!processResponse.ok) {
        throw new Error(`Processing failed for patient ${individualPatient.name}`);
      }
      
      return processResponse.json();
    });
    
    const results = await Promise.all(processPromises);
    
    const updatedPatientData = loadedPatientData.map((patient, index) => {
      if (index < results.length) {
        return {
          ...patient,
          ...results[index]
        };
      }
      return patient;
    });
    
    setLoadedPatientData(updatedPatientData);
    setNameUpdateSuccess("Transcription processed for all patients");
    setTimeout(() => setNameUpdateSuccess(null), 3000);
    
  } catch (error) {
    console.error('Processing error:', error);
    setError('Failed to process transcription for all patients');
  } finally {
    setIsTranscribing(false);
  }
};

  const getSupportedMimeType = () => {
    const types = [
      'audio/webm',
      'audio/webm;codecs=opus',
      'audio/ogg;codecs=opus',
      'audio/wav',
      'audio/mp4'
    ];
    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        console.log(`Using supported MIME type: ${type}`);
        return type;
      }
    }
    throw new Error('No supported audio MIME types found');
  };

  // WebSocket setup
  const setupWebSocket = () => {
    return new Promise((resolve, reject) => {
      try {
        if (webSocket.current?.readyState === WebSocket.OPEN) {
          webSocket.current.close();
        }
        
        let url = wsUrl;
        url = `${wsUrl}?language=${selectedLanguage}`;
       
        console.log('Connecting to WebSocket:', url);
        webSocket.current = new WebSocket(url);
  
        webSocket.current.onopen = () => {
          console.log('WebSocket connected successfully');
          setError(null);
          resolve(webSocket.current);
        };
  
        webSocket.current.onmessage = (event) => {
          // handle pings
          if (event.data === 'ping') {
            webSocket.current.send('pong');
            return;
          }
          // handle transcripts
          try {
            const response = JSON.parse(event.data);
            
            // Handle language change confirmation
            if (response.type === 'language_changed') {
              console.log(`Language changed to: ${response.language}`);
              return;
            }
            
            // Update detected language if available
            if (response.language) {
              console.log("Detected language:", response.language);
              setDetectedLanguage(response.language);
            }
            
            if (response.type === 'partial') {
              // Show partial transcripts in real time
              const partialChunk = (response.transcript || '').trim();
              if (partialChunk) {
                latestPartialTranscriptRef.current = partialChunk;
                lastPartialTranscriptAtRef.current = Date.now();
                setTranscriptProgress(partialChunk);
              }
            } else if (response.type === 'final') {
              // Accumulate final transcripts
              const finalChunk = (response.transcript || '').trim();
              if (finalChunk) {
                const updatedTranscript = finalTranscriptRef.current
                  ? `${finalTranscriptRef.current} ${finalChunk}`
                  : finalChunk;
                finalTranscriptRef.current = updatedTranscript;
                lastFinalTranscriptAtRef.current = Date.now();
                setFinalTranscript(updatedTranscript);
              }
              // Optionally clear partial display
              setTranscriptProgress('');
            }
          } catch (error) {
            console.error('Error parsing WebSocket message:', error);
          }
        };
  
        webSocket.current.onclose = (event) => {
          console.log('WebSocket closed:', event);
          if (isRecordingRef.current) {
            // Attempt to reconnect if still recording
            console.log('Attempting to reconnect...');
            setTimeout(() => {
              if (isRecordingRef.current) {
                setupWebSocket()
                  .then(() => console.log('Reconnected successfully'))
                  .catch(error => console.error('Reconnection failed:', error));
              }
            }, 1000);
          }
        };
  
        webSocket.current.onerror = (error) => {
          console.error('WebSocket error:', error);
          setError('Connection failed. Please try again.');
          reject(error);
          if (webSocket.current?.readyState !== WebSocket.CLOSED) {
            webSocket.current.close();
          }
        };
  
        // Add connection timeout
        setTimeout(() => {
          if (webSocket.current?.readyState !== WebSocket.OPEN) {
            reject(new Error('WebSocket connection timeout'));
            if (webSocket.current) {
              webSocket.current.close();
            }
          }
        }, 5000);
  
      } catch (error) {
        console.error('Setup WebSocket error:', error);
        setError('Failed to setup connection. Please try again.');
        reject(error);
      }
    });
  };

  // Start recording
  const startRecording = async () => {
    try {
      // Reset transcripts
      setTranscriptProgress('');
      setFinalTranscript('');
      finalTranscriptRef.current = '';
      lastFinalTranscriptAtRef.current = null;
      latestPartialTranscriptRef.current = '';
      lastPartialTranscriptAtRef.current = null;
      setIsTranscribing(false);
      setPendingRecordingDuration(0);
      setActiveRecordingDuration(0);
      recordingStartedAtRef.current = Date.now();

      // Get mic permission
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 16000
        }
      });

      audioStream.current = stream;

      // Open WebSocket
      await setupWebSocket();

      // Check if we need to use Web Audio API for Persian (Gladia requires PCM)
      const usePCM = selectedLanguage === 'fa';

      if (usePCM) {
        console.log('Using Web Audio API for Persian (PCM format)');

        // Set recording state immediately
        setIsRecording(true);
        isRecordingRef.current = true;
        onRecordingStateChange?.(true);

        // Create audio context for PCM extraction
        audioContext.current = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 16000 });
        const source = audioContext.current.createMediaStreamSource(stream);

        // Create a script processor to extract PCM data
        const bufferSize = 4096;
        audioProcessor.current = audioContext.current.createScriptProcessor(bufferSize, 1, 1);

        let packetCount = 0;
        audioProcessor.current.onaudioprocess = (e) => {
          if (!isRecordingRef.current || webSocket.current?.readyState !== WebSocket.OPEN) {
            return;
          }

          packetCount++;
          if (packetCount <= 5) {
            console.log(`Processing audio packet #${packetCount}`);
          }

          // Get the PCM data from the input buffer
          const inputData = e.inputBuffer.getChannelData(0);

          // Convert Float32 PCM to Int16 PCM
          const pcm16 = new Int16Array(inputData.length);
          for (let i = 0; i < inputData.length; i++) {
            // Clamp to [-1, 1] and convert to 16-bit integer
            const s = Math.max(-1, Math.min(1, inputData[i]));
            pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
          }

          // Send as binary data
          try {
            webSocket.current.send(pcm16.buffer);
            if (packetCount <= 5) {
              console.log(`Sent PCM audio: ${pcm16.buffer.byteLength} bytes`);
            }
          } catch (error) {
            console.error('Error sending PCM audio:', error);
          }
        };

        // Connect the audio graph
        source.connect(audioProcessor.current);
        audioProcessor.current.connect(audioContext.current.destination);

        console.log('Web Audio API setup complete, audio graph connected');

      } else {
        console.log('Using MediaRecorder for non-Persian languages');

        // Create MediaRecorder for other languages
        const mimeType = getSupportedMimeType();
        mediaRecorder.current = new MediaRecorder(stream, {
          mimeType: mimeType,
          audioBitsPerSecond: 16000
        });

        // Handle audio chunks
        mediaRecorder.current.ondataavailable = async (event) => {
          if (event.data.size > 0 && webSocket.current?.readyState === WebSocket.OPEN) {
            try {
              webSocket.current.send(event.data);
            } catch (error) {
              console.error('Error sending audio data:', error);
              if (error.message.includes('closed')) {
                try {
                  await setupWebSocket();
                } catch (reconnectError) {
                  console.error('Failed to reconnect:', reconnectError);
                }
              }
            }
          }
        };

        mediaRecorder.current.start(250); // chunk size
        setIsRecording(true);
        isRecordingRef.current = true;
        onRecordingStateChange?.(true);
      }

      setError(null);

      console.log(`Starting recording with language: ${selectedLanguage}`);

    } catch (error) {
      console.error('Recording error:', error);
      setError(`Failed to start recording: ${error.message}`);
      recordingStartedAtRef.current = null;
      // Cleanup
      if (mediaRecorder.current) {
        mediaRecorder.current.stream.getTracks().forEach(track => track.stop());
      }
      if (webSocket.current) {
        webSocket.current.close();
      }
      setIsRecording(false);
      isRecordingRef.current = false;
      onRecordingStateChange?.(false);
      setTranscriptProgress('');
    }
  };

  // Stop recording
  const stopRecording = async () => {
    if (isStoppingRecordingRef.current) {
      console.log('Stop already in progress, ignoring duplicate stop click');
      return;
    }
    isStoppingRecordingRef.current = true;
    console.log('Stopping recording...');
    try {
      const completedRecordingDuration = getCompletedRecordingDuration(recordingStartedAtRef.current);
      recordingStartedAtRef.current = null;
      setActiveRecordingDuration(0);
      setPendingRecordingDuration(completedRecordingDuration);
      setIsRecording(false);
      isRecordingRef.current = false;
      onRecordingStateChange?.(false);

      // Flush final blob from MediaRecorder before tearing down the stream.
      await stopMediaRecorderAndFlush();

      // Clean up Web Audio API if used
      if (audioProcessor.current) {
        audioProcessor.current.disconnect();
        audioProcessor.current = null;
      }
      if (audioContext.current) {
        audioContext.current.close();
        audioContext.current = null;
      }
      if (audioStream.current) {
        audioStream.current.getTracks().forEach(track => track.stop());
        audioStream.current = null;
      }

      const { transcript: transcriptToProcess, source: transcriptSource } = await flushPendingTranscript();
      if (transcriptSource === 'partial' && transcriptToProcess.length > 0) {
        console.warn('Using partial transcript fallback because no final transcript was received before stop');
      }

      if (webSocket.current) {
        webSocket.current.close();
      }
      setTranscriptProgress('');

      if (transcriptToProcess.length > 0) {
        // If this is a combined patient session, skip detection
        if (patient?.is_combined) {
          setIsTranscribing(true);
          try {
            setPendingRecordingDuration(0);
            await handleCombinedTranscriptionProcessing(transcriptToProcess);
          } catch (error) {
            console.error('Processing error:', error);
            setError('Failed to process transcription for combined patients');
          } finally {
            setIsTranscribing(false);
          }
          return;
        }
        
        setIsTranscribing(true);
        
        try {
          // Save the original transcription for later use
          setOriginalTranscription(transcriptToProcess);
          
          const processResponse = await fetch('/api/process-transcription', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              transcription: transcriptToProcess,
              previous_note: editedData,
              patient_id: patient.id,
              location: currentLocation || patient.location,
              recording_duration: completedRecordingDuration,
              section_styles: sectionStyles,
              userSettings: { ...userSettings, useBlainesPrompts: useBlainesPrompts },
              useFormatted: true,
              selected_template_id: selectedTemplate?._id || null
            })
          });
        
          if (!processResponse.ok) {
            throw new Error('Processing failed');
          }
        
          const processedData = await processResponse.json();

          // Check if multiple patients were detected by AI
          if (processedData.multi_patient_suspected) {
            console.log("Multiple patients detected with confidence:", 
              processedData.detection_info?.confidence);
            setMultiPatientDetection(processedData.detection_info);
            setShowMultiPatientConfirm(true);
            setIsTranscribing(false);
            return;
          }
          
          // Normal single patient processing
          if (processedData.name_updated && processedData.extracted_name) {
            setNameUpdateSuccess(`Patient name updated to: ${processedData.extracted_name}`);
            setTimeout(() => setNameUpdateSuccess(null), 3000);
          }

          // Update editedData to include custom sections
          const updatedEditedData = {
            visit_summary: processedData.visit_summary || '',
            hpi: processedData.hpi || '',
            assessment_plan: processedData.assessment_plan || '',
            ros: processedData.ros || '',
            suggested_tests: processedData.suggested_tests || [],
            icd_codes: processedData.icd_codes || [],
            cpt_codes: processedData.cpt_codes || [],
            em_codes: processedData.em_codes || [],
            physical_exam: processedData.physical_exam || [],
            after_visit_summary: processedData.after_visit_summary || [],
            em_education: processedData.em_education || [],
            additional_mdm: processedData.additional_mdm || [],
            // MA Section fields
            ma_medications: processedData.ma_medications || [],
            ma_inhouse_labs: processedData.ma_inhouse_labs || [],
            ma_sendout_labs: processedData.ma_sendout_labs || [],
            ma_procedures: processedData.ma_procedures || [],
          };

          // Add custom sections data
          customSections.forEach(section => {
            const customKey = `custom_${section.id}`;
            if (processedData[customKey]) {
              updatedEditedData[customKey] = processedData[customKey];
            }
          });

          setTotalRecordingDuration(prev => prev + completedRecordingDuration);
          setPendingRecordingDuration(0);
          setEditedData(updatedEditedData);
          onPatientUpdate(processedData);
        } catch (error) {
          console.error('Processing error:', error);
          setPendingRecordingDuration(0);
          setError('Failed to process transcription');
        } finally {
          setIsTranscribing(false);
        }
      } else {
        setPendingRecordingDuration(0);
        console.log('No final transcript to process');
      }
    } catch (error) {
      console.error('Error stopping recording:', error);
      setPendingRecordingDuration(0);
      setError('Error stopping recording. Please refresh the page.');
    } finally {
      isStoppingRecordingRef.current = false;
    }
  };

  const formatDateForDisplay = (dateStr) => {
    if (!dateStr) return '';
    // Handle both yyyy-MM-dd and MM/DD/YYYY formats
    if (dateStr.includes('-')) {
      const [year, month, day] = dateStr.split('-');
      return `${month}/${day}/${year}`;
    }
    return dateStr;
  };

  const formatDateForInput = (dateStr) => {
    if (!dateStr) return '';
    // Handle both yyyy-MM-dd and MM/DD/YYYY formats
    if (dateStr.includes('/')) {
      const [month, day, year] = dateStr.split('/');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return dateStr;
  };

  const handleDownloadPDF = async (patientId) => {
    try {
      const response = await fetch(`/api/patients/${patientId}/download-pdf`, {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to download PDF');
        return;
      }

      // Create a blob from the response
      const blob = await response.blob();

      // Create a temporary URL for the blob
      const url = window.URL.createObjectURL(blob);

      // Create a temporary anchor element and trigger download
      const a = document.createElement('a');
      a.href = url;
      a.download = `miracletouch_intake_${patient.patient_id || patientId}.pdf`;
      document.body.appendChild(a);
      a.click();

      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setNameUpdateSuccess('PDF downloaded successfully');
      setTimeout(() => setNameUpdateSuccess(null), 3000);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      setError('Failed to download PDF');
    }
  };

  const handleEditStart = () => {
    setIsEditingInfo(true);
    setError(null);
    
    if (patient?.is_combined && loadedPatientData.length > 0) {
      // Set up editing for combined patients
      const combinedEdits = loadedPatientData.map(individualPatient => {
        // Parse individual patient name
        let firstName = individualPatient.first_name || '';
        let lastName = individualPatient.last_name || '';
        
        if (!firstName && !lastName && individualPatient.name) {
          const nameParts = individualPatient.name.split(' ');
          firstName = nameParts[0] || '';
          lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';
        }
        
        // Parse DOB
        let dobMonth = '', dobDate = '', dobYear = '';
        if (individualPatient.dob) {
          const dobParts = individualPatient.dob.split('/');
          if (dobParts.length === 3) {
            dobMonth = dobParts[0];
            dobDate = dobParts[1];
            dobYear = dobParts[2];
          }
        }
        
        return {
          id: individualPatient.id,
          name: individualPatient.name,
          first_name: firstName,
          last_name: lastName,
          dobMonth,
          dobDate,
          dobYear,
          pronouns: individualPatient.pronouns || ''
        };
      });
      
      setEditedCombinedPatientsInfo(combinedEdits);
    } else {
      // Original single patient editing
      const [firstName, ...rest] = patient.name.split(' ');
      const lastName = rest.length > 0 ? rest[rest.length - 1] : '';
      
      // Parse the date components
      let dobMonth = '', dobDate = '', dobYear = '';
      if (patient.dob) {
        const [month, day, year] = patient.dob.split('/');
        dobMonth = month;
        dobDate = day;
        dobYear = year;
      }
    
      setEditedPatientInfo({
        first_name: firstName || '',
        last_name: lastName || '',
        dobMonth: dobMonth,
        dobDate: dobDate,
        dobYear: dobYear,
        pronouns: patient.pronouns || ''
      });
    }
  };
  
  // Validation functions
  const isValidDate = (month, day, year) => {
    if (!month || !day || !year) return false;
    
    const date = new Date(year, month - 1, day);
    const currentDate = new Date();
    const minDate = new Date();
    minDate.setFullYear(currentDate.getFullYear() - 120);
  
    if (date.getMonth() !== month - 1 || date.getDate() !== parseInt(day)) {
      return 'invalid';
    }
  
    if (date > currentDate) {
      return 'future';
    }
  
    if (date < minDate) {
      return 'past';
    }
  
    return 'valid';
  };
  
  const isValidEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.(?:com|edu|org|net|gov|mil|biz|info|io|co|us|uk|ca|au|de|jp|fr|it|es|nl|ch|se|no|dk|fi|ie|nz)$/i;
    return emailRegex.test(email);
  };
  
  const formatPhoneNumber = (value) => {
    const phoneNumber = value.replace(/[^\d+]/g, '');
    
    if (phoneNumber.length === 0) return '';
    if (phoneNumber.length <= 3) return `(${phoneNumber}`;
    if (phoneNumber.length <= 6) return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };
  
  const getCombinedDOB = (month, day, year) => {
    if (!month || !day || !year) return '';
    return `${month}/${day}/${year}`;
  };
  
  const handleEditCancel = () => {
    setIsEditingInfo(false);
    setError(null);
    setValidationErrors({});
  };

  const hasPatientInfoChanged = () => {
    const originalDOB = patient.dob || '';
    const editedDOB = getCombinedDOB(
      editedPatientInfo.dobMonth,
      editedPatientInfo.dobDate,
      editedPatientInfo.dobYear
    );
  
    return (
      editedPatientInfo.first_name.trim() !== (patient.first_name || '').trim() ||
      editedPatientInfo.last_name.trim() !== (patient.last_name || '').trim() ||
      editedDOB !== originalDOB ||
      editedPatientInfo.email !== (patient.email || '') ||
      editedPatientInfo.phone !== (patient.phone || '') ||
      editedPatientInfo.legal_sex !== (patient.legal_sex || '') ||
      editedPatientInfo.birth_sex !== (patient.birth_sex || '')
    );
  };

  const handlePronounsChange = async (newPronouns) => {
    try {
      // Set loading state
      setIsUpdatingPronouns(true);
      setError(null);
      
      const oldPronouns = patient.pronouns || '';
      
      // Update pronouns in database
      const response = await fetch(`/api/patients/${patient.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          pronouns: newPronouns
        }),
      });
  
      if (!response.ok) {
        throw new Error('Failed to update pronouns');
      }
  
      const updatedPatient = await response.json();
      
      // If the patient has notes, update them with the new pronouns
      if (editedData.hpi?.length || editedData.assessment_plan?.length || editedData.ros?.length) {
        // Create a copy of the patient's data
        const updatedNotes = {
          visit_summary: editedData.visit_summary || [],
          hpi: editedData.hpi || [],
          assessment_plan: editedData.assessment_plan || [],
          ros: editedData.ros || [],
          physical_exam: editedData.physical_exam || [],
          suggested_tests: editedData.suggested_tests || [],
          icd_codes: editedData.icd_codes || [],
          cpt_codes: editedData.cpt_codes || [],
          em_codes: editedData.em_codes || [],
          after_visit_summary: editedData.after_visit_summary || [],
          em_education: editedData.em_education || [],
        };
        
        // Apply pronouns to each text field
        if (Array.isArray(updatedNotes.hpi)) {
          updatedNotes.hpi = updatedNotes.hpi.map(item => 
            applyPronouns(item, newPronouns)
          );
        }
        
        if (Array.isArray(updatedNotes.assessment_plan)) {
          updatedNotes.assessment_plan = updatedNotes.assessment_plan.map(item => 
            applyPronouns(item, newPronouns)
          );
        }
        
        if (Array.isArray(updatedNotes.ros)) {
          updatedNotes.ros = updatedNotes.ros.map(item => 
            applyPronouns(item, newPronouns)
          );
        }
        
        if (Array.isArray(updatedNotes.physical_exam)) {
          updatedNotes.physical_exam = updatedNotes.physical_exam.map(item => 
            applyPronouns(item, newPronouns)
          );
        }
        
        if (Array.isArray(updatedNotes.visit_summary)) {
          updatedNotes.visit_summary = updatedNotes.visit_summary.map(item => 
            applyPronouns(item, newPronouns)
          );
        }

        if (Array.isArray(updatedNotes.after_visit_summary)) {
          updatedNotes.after_visit_summary = updatedNotes.after_visit_summary.map(item => 
            applyPronouns(item, newPronouns)
          ); 
          
        } else if (typeof updatedNotes.visit_summary === 'string') {
          updatedNotes.visit_summary = applyPronouns(updatedNotes.visit_summary, newPronouns);
        }
        
        // Save the updated notes to the backend
        const notesResponse = await fetch(`/api/patients/${patient.id}/soap`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(updatedNotes),
        });
        
        if (!notesResponse.ok) {
          throw new Error('Failed to update notes with new pronouns');
        }
        
        // Get the updated patient with new notes
        const updatedPatientWithNotes = await notesResponse.json();
        
        // Update the local state
        setEditedData(updatedPatientWithNotes);
        onPatientUpdate(updatedPatientWithNotes);
        
        setNameUpdateSuccess(`Pronouns updated to: ${newPronouns}`);
      } else {
        // If no notes, just update the patient data
        onPatientUpdate(updatedPatient);
        setNameUpdateSuccess(`Pronouns updated to: ${newPronouns}`);
      }
      
      setTimeout(() => setNameUpdateSuccess(null), 3000);
    } catch (err) {
      console.error('Error updating pronouns:', err);
      setError('Failed to update pronouns');
    } finally {
      setIsUpdatingPronouns(false);
    }
  };
  
  const handlePatientInfoUpdate = async () => {
    // Validation logic for single or combined patients
    let hasValidationErrors = false;
    let newValidationErrors = {};
    
    if (patient?.is_combined && editedCombinedPatientsInfo.length > 0) {
      // Validate all combined patients
      for (const patientInfo of editedCombinedPatientsInfo) {
        if (!patientInfo.first_name || !patientInfo.last_name || 
            !patientInfo.dobMonth || !patientInfo.dobDate || !patientInfo.dobYear) {
          hasValidationErrors = true;
          newValidationErrors.combined = "All patient information must be completed";
          break;
        }
        
        // Validate dates for each patient
        const month = parseInt(patientInfo.dobMonth, 10);
        const day = parseInt(patientInfo.dobDate, 10);
        const year = parseInt(patientInfo.dobYear, 10);
        
        const dateValidation = isValidDate(month, day, year);
        if (dateValidation !== 'valid') {
          hasValidationErrors = true;
          newValidationErrors.combined = "Please enter valid dates for all patients";
          break;
        }
      }
    } else {
      // Original single patient validation
      const email = editedPatientInfo.email || '';
      const phone = editedPatientInfo.phone || '';
      const dobMonth = editedPatientInfo.dobMonth || '';
      const dobDate = editedPatientInfo.dobDate || '';
      const dobYear = editedPatientInfo.dobYear || '';
      
      if (email.trim() !== '' && !isValidEmail(email.trim())) {
        newValidationErrors.email = 'Please enter a valid email address';
      }
  
      if (phone !== '' && phone.replace(/\D/g, '').length !== 10) {
        newValidationErrors.phone = 'Please enter a valid phone number';
      }
  
      if (!dobMonth.trim() || !dobDate.trim() || !dobYear.trim()) {
        newValidationErrors.dob = 'Complete date of birth is required';
      } else {
        const month = parseInt(dobMonth, 10);
        const day = parseInt(dobDate, 10);
        const year = parseInt(dobYear, 10);
        
        const dateValidation = isValidDate(month, day, year);
        
        if (dateValidation === 'future') {
          newValidationErrors.dob = 'Date of birth cannot be in the future';
        } else if (dateValidation === 'past') {
          newValidationErrors.dob = 'Please enter a valid date of birth';
        } else if (dateValidation === 'invalid') {
          newValidationErrors.dob = 'Please enter a valid date';
        }
      }
    }
    
    setValidationErrors(newValidationErrors);
    
    if (hasValidationErrors) {
      return;
    }
  
    // Check if any information has changed (for single patients)
    if (!patient?.is_combined && !hasPatientInfoChanged()) {
      setIsEditingInfo(false);
      return;
    }
    
    setIsSaving(true);
    
    try {
      if (patient?.is_combined && editedCombinedPatientsInfo.length > 0) {
        // Update all combined patients
        const updatePromises = editedCombinedPatientsInfo.map(async (patientInfo) => {
          const combinedDOB = getCombinedDOB(
            patientInfo.dobMonth,
            patientInfo.dobDate,
            patientInfo.dobYear
          );
          
          const response = await fetch(`/api/patients/${patientInfo.id}`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({
              first_name: patientInfo.first_name,
              last_name: patientInfo.last_name,
              date_of_birth: combinedDOB,
              pronouns: patientInfo.pronouns
            }),
          });
          
          if (!response.ok) {
            throw new Error(`Failed to update patient ${patientInfo.id}`);
          }
          
          return response.json();
        });
        
        await Promise.all(updatePromises);
        
        // Refresh patient data
        const refreshedData = await Promise.all(
          loadedPatientData.map(async (p) => {
            const response = await fetch(`/api/patients/${p.id}`, {
              credentials: 'include'
            });
            return response.ok ? response.json() : null;
          })
        );
        
        setLoadedPatientData(refreshedData.filter(p => p !== null));
        
        // Update the combined patient name in parent component
        const combinedNames = editedCombinedPatientsInfo
          .map(p => `${p.first_name} ${p.last_name}`)
          .join(' + ');
          
        const updatedCombinedPatient = {
          ...patient,
          name: combinedNames,
          combined_names: combinedNames
        };
        
        onPatientUpdate(updatedCombinedPatient);
      } else {
        // Original single patient update logic
        const changedFields = {};
        if (editedPatientInfo.first_name.trim() !== (patient.first_name || '').trim())
          changedFields.first_name = editedPatientInfo.first_name;
        if (editedPatientInfo.last_name.trim() !== (patient.last_name || '').trim())
          changedFields.last_name = editedPatientInfo.last_name;
        if (editedPatientInfo.middle_initial !== (patient.middle_initial || ''))
          changedFields.middle_initial = editedPatientInfo.middle_initial;
          
        const combinedDOB = getCombinedDOB(
          editedPatientInfo.dobMonth,
          editedPatientInfo.dobDate,
          editedPatientInfo.dobYear
        );
        
        if (combinedDOB !== patient.dob)
          changedFields.date_of_birth = combinedDOB;
          
        if (editedPatientInfo.legal_sex !== patient.legal_sex)
          changedFields.legal_sex = editedPatientInfo.legal_sex;
        if (editedPatientInfo.birth_sex !== patient.birth_sex)
          changedFields.birth_sex = editedPatientInfo.birth_sex;
        if (editedPatientInfo.email !== patient.email)
          changedFields.email = editedPatientInfo.email;
        if (editedPatientInfo.phone !== patient.phone)
          changedFields.phone = editedPatientInfo.phone;
        if (editedPatientInfo.pronouns !== patient.pronouns)
          changedFields.pronouns = editedPatientInfo.pronouns;
  
        const response = await fetch(`/api/patients/${patient.id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(changedFields)
        });
  
        if (!response.ok) {
          throw new Error('Failed to update patient information');
        }
  
        const updatedPatient = await response.json();
        onPatientUpdate(updatedPatient);
      }
      
      setIsEditingInfo(false);
      setError(null);
      setValidationErrors({});
    } catch (err) {
      console.error('Error updating patient info:', err);
      setError('Failed to update patient information');
    } finally {
      setIsSaving(false);
    }
  };
  
  // DOB input handler
  const handleDOBChange = (e) => {
    const { name, value } = e.target;
    let newValue = value.replace(/\D/g, '');
  
    // Apply specific constraints based on the field
    if (name === 'dobMonth') {
      if (newValue.length === 1 && parseInt(newValue) > 1) {
        newValue = '0' + newValue;
      }
      if (parseInt(newValue) > 12) {
        newValue = '12';
      }
    } else if (name === 'dobDate') {
      if (newValue.length === 1 && parseInt(newValue) > 3) {
        newValue = '0' + newValue;
      }
      if (parseInt(newValue) > 31) {
        newValue = '31';
      }
    } else if (name === 'dobYear') {
      if (newValue.length > 4) {
        newValue = newValue.slice(0, 4);
      }
    }
  
    setEditedPatientInfo(prev => ({
      ...prev,
      [name]: newValue
    }));
  };
  
  // Phone input handler
  const handlePhoneChange = (e) => {
    const { value } = e.target;
    const formattedPhone = formatPhoneNumber(value);
    if (formattedPhone.replace(/\D/g, '').length <= 10) {
      setEditedPatientInfo(prev => ({
        ...prev,
        phone: formattedPhone
      }));
    }
  };
  
  // Render validation error message
  const renderErrorMessage = (fieldName) => {
    return validationErrors[fieldName] ? (
      <span className="text-red-500 text-sm mt-1">{validationErrors[fieldName]}</span>
    ) : null;
  };

   // Function to update text with new pronouns
const updateTextWithPronouns = (text, oldPronouns, newPronouns) => {
  if (!text || typeof text !== 'string') return text;
  
  // Define pronoun mappings
  const pronounMappings = {
    'He/him': {
      subject: 'he',
      object: 'him',
      possessive: 'his',
      possessiveAdj: 'his',
      reflexive: 'himself'
    },
    'She/her': {
      subject: 'she',
      object: 'her',
      possessive: 'hers',
      possessiveAdj: 'her',
      reflexive: 'herself'
    },
    'They/them': {
      subject: 'they',
      object: 'them',
      possessive: 'theirs',
      possessiveAdj: 'their',
      reflexive: 'themselves'
    }
  };
  
  // Get old and new pronoun objects
  const oldPronounObj = pronounMappings[oldPronouns];
  const newPronounObj = pronounMappings[newPronouns];
  
  // If either pronoun is not in our mapping, return original text
  if (!oldPronounObj || !newPronounObj) return text;
  
  // Replace pronouns in text (case-insensitive)
  let updatedText = text;
  
  // Replace subject pronouns (he/she/they)
  const subjectRegex = new RegExp(`\\b${oldPronounObj.subject}\\b`, 'gi');
  updatedText = updatedText.replace(subjectRegex, (match) => {
    // Preserve capitalization
    return match[0].toUpperCase() === match[0] 
      ? newPronounObj.subject.charAt(0).toUpperCase() + newPronounObj.subject.slice(1) 
      : newPronounObj.subject;
  });
  
  // Replace object pronouns (him/her/them)
  const objectRegex = new RegExp(`\\b${oldPronounObj.object}\\b`, 'gi');
  updatedText = updatedText.replace(objectRegex, newPronounObj.object);
  
  // Replace possessive pronouns (his/hers/theirs)
  const possessiveRegex = new RegExp(`\\b${oldPronounObj.possessive}\\b`, 'gi');
  updatedText = updatedText.replace(possessiveRegex, newPronounObj.possessive);
  
  // Replace possessive adjectives (his/her/their)
  const possessiveAdjRegex = new RegExp(`\\b${oldPronounObj.possessiveAdj}\\b`, 'gi');
  updatedText = updatedText.replace(possessiveAdjRegex, newPronounObj.possessiveAdj);
  
  // Replace reflexive pronouns (himself/herself/themselves)
  const reflexiveRegex = new RegExp(`\\b${oldPronounObj.reflexive}\\b`, 'gi');
  updatedText = updatedText.replace(reflexiveRegex, newPronounObj.reflexive);
  
  return updatedText;
};

// Function to update all notes with new pronouns
const updateNotesWithPronouns = (notes, oldPronouns, newPronouns) => {
  if (!oldPronouns || !newPronouns || oldPronouns === newPronouns) {
    return notes; // No change needed
  }
  
  const updatedNotes = { ...notes };
  
  // Update each section
  if (Array.isArray(updatedNotes.hpi)) {
    updatedNotes.hpi = updatedNotes.hpi.map(item => 
      updateTextWithPronouns(item, oldPronouns, newPronouns)
    );
  }
  
  if (Array.isArray(updatedNotes.assessment_plan)) {
    updatedNotes.assessment_plan = updatedNotes.assessment_plan.map(item => 
      updateTextWithPronouns(item, oldPronouns, newPronouns)
    );
  }
  
  if (Array.isArray(updatedNotes.ros)) {
    updatedNotes.ros = updatedNotes.ros.map(item => 
      updateTextWithPronouns(item, oldPronouns, newPronouns)
    );
  }
  
  if (Array.isArray(updatedNotes.physical_exam)) {
    updatedNotes.physical_exam = updatedNotes.physical_exam.map(item => 
      updateTextWithPronouns(item, oldPronouns, newPronouns)
    );
  }

  if (Array.isArray(updatedNotes.after_visit_summary)) {
    updatedNotes.after_visit_summary = updatedNotes.after_visit_summary.map(item => 
      updateTextWithPronouns(item, oldPronouns, newPronouns)
    );
  }
  
  if (Array.isArray(updatedNotes.visit_summary)) {
    updatedNotes.visit_summary = updatedNotes.visit_summary.map(item => 
      updateTextWithPronouns(item, oldPronouns, newPronouns)
    );
  } else if (typeof updatedNotes.visit_summary === 'string') {
    updatedNotes.visit_summary = updateTextWithPronouns(
      updatedNotes.visit_summary, 
      oldPronouns, 
      newPronouns
    );
  }
  
  return updatedNotes;
};

const handleCombinedPatientInfoChange = (index, field, value) => {
  const updatedPatients = [...editedCombinedPatientsInfo];
  updatedPatients[index] = {
    ...updatedPatients[index],
    [field]: value
  };
  setEditedCombinedPatientsInfo(updatedPatients);
};

  // (Optional) Example: "Send to EHR" button
  // You can adapt this to your actual endpoint / logic
  const handleSendToEHR = async () => {
    const cooldownUntil = ehrCooldownUntilRef.current || ehrCooldownUntil;
    const now = Date.now();
    if (cooldownUntil && now < cooldownUntil) {
      const remainingSeconds = Math.ceil((cooldownUntil - now) / 1000);
      showEhrCooldownNotice(`Please wait ${remainingSeconds}s before sending again.`);
      return;
    }

    if (isSendingToEHR || isTranscribing || isRecording) {
      return;
    }

    startEhrCooldown();
    setIsSendingToEHR(true);
    setError(null);
  
    try {
      const currentSettings = userSettings;
      
      // Add a special flag to indicate we're sending empty medications
      // This will tell the backend to handle the medication field specially
      const response = await fetch(`/api/patients/${patient.id}/send-to-ehr`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          settings: {
            showSnomedCodes: currentSettings.showSnomedCodes,
            showICDCodes: currentSettings.showICDCodes,
            showCPTCodes: currentSettings.showCPTCodes,
            // Add this flag to indicate we should handle medications specially
            emptyMedicationsHandling: true
          },
          customSectionSendWith: customSectionSendWith
        }),
        credentials: 'include'
      });
  
      if (!response.ok) {
        throw new Error('Failed to send to EHR');
      }
  
      const result = await response.json();
      if (onEHRStatusChange) {
        onEHRStatusChange(patient.id, true);
      }
      onPatientUpdate(result.patient);

      // For confdemo patients, populate EMR with data
      if (result.emr_data) {
        // Store in localStorage for EMR component to access
        localStorage.setItem('emr-patient-data', JSON.stringify(result.emr_data));

        // Dispatch custom event to notify EMR component
        window.dispatchEvent(new CustomEvent('emr-patient-update', {
          detail: { patient: result.emr_data }
        }));

        console.log('EMR data populated for confdemo patient:', result.emr_data);
      }

      setShowSentSuccess(true);
      setTimeout(() => {
        setShowSentSuccess(false);
      }, 5000); // Reset after 5 seconds
    } catch (error) {
      console.error('Error sending to EHR:', error);
      setError('Failed to send to EHR. Please try again.');
    } finally {
      setIsSendingToEHR(false);
    }
  };

  useEffect(() => {
    const eventSource = new EventSource('https://dashboard.fluency.health/api/receive_err');
    
    eventSource.addEventListener('ehr-match-error', (event) => {
      const errorData = JSON.parse(event.data);
      setEhrErrorStatus(errorData);
      
      // Auto-hide the error after 5 seconds
      setTimeout(() => {
        setEhrErrorStatus(null);
      }, 3500);
    });

    return () => {
      eventSource.close();
    };
  }, []);

  if (!patient) return null;

  return (
    <div className="p-6 bg-white rounded-lg pb-20" ref={containerRef}>
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-600 rounded-md">
          {error}
        </div>
      )}

      {nameUpdateSuccess && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 text-green-600 rounded-md">
          {nameUpdateSuccess}
        </div>
      )}

      <MultiPatientConfirmDialog
        isOpen={showMultiPatientConfirm}
        onClose={() => setShowMultiPatientConfirm(false)}
        detectionInfo={multiPatientDetection}
        onConfirm={handleConfirmMultiPatients}
        onDecline={handleDeclineMultiPatients}
      />

      {/* Transcription Modal */}
      {showTranscriptionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-xl font-semibold">Voice Transcription</h2>
              <button
                onClick={() => setShowTranscriptionModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 flex-1 overflow-auto">
              <div className="bg-gray-50 p-4 rounded-lg mb-4 max-h-60 overflow-y-auto">
                <p className="text-gray-800 whitespace-pre-wrap">{originalTranscription}</p>
              </div>
              
              {isConvertingTranscription ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-center">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                  </div>
                  <div className="text-center">
                    <p className="text-gray-600 mb-2">Converting transcription to notes...</p>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
                        style={{ width: `${conversionProgress}%` }}
                      ></div>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">{conversionProgress}% complete</p>
                  </div>
                </div>
              ) : (
                null
                /* <div className="flex justify-center">
                  <button
                    onClick={handleConvertTranscription}
                    className="px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center gap-2"
                  >
                    <Globe className="w-5 h-5" />
                    Convert Transcription Into Notes
                  </button>
                </div> */
              )}
            </div>
          </div>
        </div>
      )}

    <div className="flex justify-between px-3 mb-6">
        <div>
        {isEditingInfo ? (
          <div className="space-y-4 mr-8">
            {patient?.is_combined && editedCombinedPatientsInfo.length > 0 ? (
              /* Combined patients editing form */
              <div>
                <h3 className="text-lg font-medium mb-3">Edit Combined Patients</h3>
                {validationErrors.combined && (
                  <div className="text-red-500 text-sm mb-2">{validationErrors.combined}</div>
                )}
                
                <div className="space-y-6">
                  {editedCombinedPatientsInfo.map((patientInfo, index) => (
                    <div key={patientInfo.id} className="p-4 border border-gray-200 rounded-lg">
                      <h4 className="font-medium mb-3">{patientInfo.name || `Patient ${index + 1}`}</h4>

                      <div className="grid grid-cols-12 gap-4 mb-3">
                        {patient?.location === 'miracletouch' ? (
                          <>
                            {/* For miracletouch: Last Name, First Name */}
                            <div className="col-span-5">
                              <label className="block text-sm text-gray-600 mb-1">Last Name</label>
                              <input
                                type="text"
                                placeholder="Last Name"
                                value={patientInfo.last_name}
                                onChange={(e) => handleCombinedPatientInfoChange(index, 'last_name', e.target.value)}
                                className="w-full px-3 py-2 border rounded-lg"
                              />
                            </div>
                            <div className="col-span-5">
                              <label className="block text-sm text-gray-600 mb-1">First Name</label>
                              <input
                                type="text"
                                placeholder="First Name"
                                value={patientInfo.first_name}
                                onChange={(e) => handleCombinedPatientInfoChange(index, 'first_name', e.target.value)}
                                className="w-full px-3 py-2 border rounded-lg"
                              />
                            </div>
                          </>
                        ) : (
                          <>
                            {/* For other locations: First Name, Last Name */}
                            <div className="col-span-5">
                              <label className="block text-sm text-gray-600 mb-1">First Name</label>
                              <input
                                type="text"
                                placeholder="First Name"
                                value={patientInfo.first_name}
                                onChange={(e) => handleCombinedPatientInfoChange(index, 'first_name', e.target.value)}
                                className="w-full px-3 py-2 border rounded-lg"
                              />
                            </div>
                            <div className="col-span-5">
                              <label className="block text-sm text-gray-600 mb-1">Last Name</label>
                              <input
                                type="text"
                                placeholder="Last Name"
                                value={patientInfo.last_name}
                                onChange={(e) => handleCombinedPatientInfoChange(index, 'last_name', e.target.value)}
                                className="w-full px-3 py-2 border rounded-lg"
                              />
                            </div>
                          </>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Date of Birth</label>
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <input
                              type="text"
                              placeholder="MM"
                              name="dobMonth"
                              value={patientInfo.dobMonth}
                              onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, '');
                                let newValue = value;
                                if (value.length === 1 && parseInt(value) > 1) {
                                  newValue = '0' + value;
                                }
                                if (parseInt(value) > 12) {
                                  newValue = '12';
                                }
                                handleCombinedPatientInfoChange(index, 'dobMonth', newValue);
                              }}
                              className="w-full px-3 py-2 border rounded-lg"
                              maxLength="2"
                            />
                          </div>
                          <div>
                            <input
                              type="text"
                              placeholder="DD"
                              name="dobDate"
                              value={patientInfo.dobDate}
                              onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, '');
                                let newValue = value;
                                if (value.length === 1 && parseInt(value) > 3) {
                                  newValue = '0' + value;
                                }
                                if (parseInt(value) > 31) {
                                  newValue = '31';
                                }
                                handleCombinedPatientInfoChange(index, 'dobDate', newValue);
                              }}
                              className="w-full px-3 py-2 border rounded-lg"
                              maxLength="2"
                            />
                          </div>
                          <div>
                            <input
                              type="text"
                              placeholder="YYYY"
                              name="dobYear"
                              value={patientInfo.dobYear}
                              onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, '');
                                const newValue = value.slice(0, 4);
                                handleCombinedPatientInfoChange(index, 'dobYear', newValue);
                              }}
                              className="w-full px-3 py-2 border rounded-lg"
                              maxLength="4"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              /* Original single patient editing form */
              <>
                <div className="grid grid-cols-12 gap-4">
                  {patient?.location === 'miracletouch' ? (
                    <>
                      {/* For miracletouch: Last Name, MI, First Name */}
                      <div className="col-span-5">
                        <input
                          type="text"
                          placeholder="Last Name"
                          value={editedPatientInfo.last_name}
                          onChange={(e) => setEditedPatientInfo(prev => ({ ...prev, last_name: e.target.value }))}
                          className="w-full px-3 py-2 border rounded-lg"
                        />
                      </div>
                      <div className="col-span-2">
                        <input
                          type="text"
                          placeholder="MI"
                          value={editedPatientInfo.middle_initial}
                          onChange={(e) => setEditedPatientInfo(prev => ({ ...prev, middle_initial: e.target.value }))}
                          className="w-full px-3 py-2 border rounded-lg"
                          maxLength={1}
                        />
                      </div>
                      <div className="col-span-5">
                        <input
                          type="text"
                          placeholder="First Name"
                          value={editedPatientInfo.first_name}
                          onChange={(e) => setEditedPatientInfo(prev => ({ ...prev, first_name: e.target.value }))}
                          className="w-full px-3 py-2 border rounded-lg"
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      {/* For other locations: First Name, MI, Last Name */}
                      <div className="col-span-5">
                        <input
                          type="text"
                          placeholder="First Name"
                          value={editedPatientInfo.first_name}
                          onChange={(e) => setEditedPatientInfo(prev => ({ ...prev, first_name: e.target.value }))}
                          className="w-full px-3 py-2 border rounded-lg"
                        />
                      </div>
                      <div className="col-span-2">
                        <input
                          type="text"
                          placeholder="MI"
                          value={editedPatientInfo.middle_initial}
                          onChange={(e) => setEditedPatientInfo(prev => ({ ...prev, middle_initial: e.target.value }))}
                          className="w-full px-3 py-2 border rounded-lg"
                          maxLength={1}
                        />
                      </div>
                      <div className="col-span-5">
                        <input
                          type="text"
                          placeholder="Last Name"
                          value={editedPatientInfo.last_name}
                          onChange={(e) => setEditedPatientInfo(prev => ({ ...prev, last_name: e.target.value }))}
                          className="w-full px-3 py-2 border rounded-lg"
                        />
                      </div>
                    </>
                  )}
                </div>
                
                {/* Date of Birth Fields */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <input
                      type="text"
                      placeholder="MM"
                      name="dobMonth"
                      value={editedPatientInfo.dobMonth}
                      onChange={handleDOBChange}
                      className={`w-full px-3 py-2 border rounded-lg ${validationErrors.dob ? 'border-red-500' : ''}`}
                      maxLength="2"
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      placeholder="DD"
                      name="dobDate"
                      value={editedPatientInfo.dobDate}
                      onChange={handleDOBChange}
                      className={`w-full px-3 py-2 border rounded-lg ${validationErrors.dob ? 'border-red-500' : ''}`}
                      maxLength="2"
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      placeholder="YYYY"
                      name="dobYear"
                      value={editedPatientInfo.dobYear}
                      onChange={handleDOBChange}
                      className={`w-full px-3 py-2 border rounded-lg ${validationErrors.dob ? 'border-red-500' : ''}`}
                      maxLength="4"
                    />
                  </div>
                </div>
                {renderErrorMessage('dob')}

                <select
                  value={editedPatientInfo.legal_sex}
                  onChange={(e) => setEditedPatientInfo(prev => ({ ...prev, legal_sex: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="">Select Sex</option>
                  <option value="M">Male</option>
                  <option value="F">Female</option>
                  <option value="O">Other</option>
                </select>

                <select
                  value={editedPatientInfo.birth_sex}
                  onChange={(e) => setEditedPatientInfo(prev => ({ ...prev, birth_sex: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg mt-2"
                >
                  <option value="">Select Birth Sex</option>
                  <option value="M">Male</option>
                  <option value="F">Female</option>
                  <option value="O">Other</option>
                </select>

                <div className="space-y-2">
                  <input
                    type="email"
                    placeholder="Email"
                    value={editedPatientInfo.email}
                    onChange={(e) => setEditedPatientInfo(prev => ({ ...prev, email: e.target.value.trim() }))}
                    className={`w-full px-3 py-2 border rounded-lg ${validationErrors.email ? 'border-red-500' : ''}`}
                  />
                  {renderErrorMessage('email')}
                  
                  <input
                    type="tel"
                    placeholder="Phone"
                    value={editedPatientInfo.phone}
                    onChange={handlePhoneChange}
                    className={`w-full px-3 py-2 border rounded-lg ${validationErrors.phone ? 'border-red-500' : ''}`}
                  />
                  {renderErrorMessage('phone')}
                </div>
              </>
            )}

            <div className="flex justify-end space-x-2">
              <button
                onClick={handleEditCancel}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                onClick={handlePatientInfoUpdate}
                className={`px-4 py-2 bg-teal-500 text-white rounded-md hover:bg-teal-600 flex items-center gap-2 ${
                  isSaving ? 'opacity-75 cursor-not-allowed' : ''
                }`}
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">Patient: {formatPatientName(patient)}</h2>
              {patient.isScheduled && (
                <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                  Scheduled
                </span>
              )}
              <button
                onClick={handleEditStart}
                className="p-1 text-gray-500 hover:text-gray-700"
                title="Edit patient information"
              >
                <Edit2 className="w-4 h-4" />
              </button>
              {patient.location === 'miracletouch' && (
                <button
                  onClick={() => handleDownloadPDF(patient.id)}
                  className="p-1 text-teal-600 hover:text-teal-700"
                  title="Download intake PDF"
                >
                  <Download className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={() => setIsDeleteDialogOpen(true)}
                className="p-1 hover:opacity-80"
                style={{ color: '#DEA9A9' }}
                title="Delete patient"
                disabled={isDeleting || userSettings.maView}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <p className="text-gray-600">
              {patient.dob && (patient.dob.includes('/') || patient.dob.includes('-'))
                ? `DOB: ${formatDateForDisplay(patient.dob)}`
                : patient.dob
                  ? `Age: ${patient.dob}${patient.dob.toString().includes('Y') ? '' : ' Y'}`
                  : ''}
            </p>
            {/*patient.legal_sex && (
              <p className="text-gray-600">Sex: {(patient.legal_sex === 'M' || patient.legal_sex === 'Male') ? 'Male' : (patient.legal_sex === 'F' || patient.legal_sex === 'Female') ? 'Female' : 'Other'}</p>
            )*/}
            {/*patient.birth_sex && (
              <p className="text-gray-600">Birth Sex: {patient.birth_sex === 'M' ? 'Male' : patient.birth_sex === 'F' ? 'Female' : 'Other'}</p>
            )*/}
            <div className="mt-1 mb-2 max-w-xs">
              {!patient?.is_combined && !userSettings.maView && (
                <div className="mt-1 mb-2 max-w-xs">
                  <PronounSelector
                    selectedPronouns={patient.pronouns || ''}
                    onPronounsChange={handlePronounsChange}
                    isDisabled={isUpdatingPronouns}
                  />
                  {isUpdatingPronouns && (
                    <div className="text-xs text-gray-500 ml-1 mt-1">Updating...</div>
                  )}
                </div>
              )}
            </div>

            {/* Note Style and Language Selector Section */}
            {!userSettings.maView && (
              <div className="mb-1 px-1 sm:px-3 mt-4 sm:mt-6">
                <p className="text-left text-sm mb-2 mx-2 sm:mx-8">Select before recording</p>

                <div className="flex flex-col sm:flex-row justify-center gap-4 sm:gap-12">
                  <LanguageSelector
                    selectedLanguage={selectedLanguage}
                    onLanguageChange={setSelectedLanguage}
                  />
                  <TemplateSelector
                    selectedTemplate={selectedTemplate}
                    onTemplateChange={setSelectedTemplate}
                  />
                </div>
              </div>
            )}

            {!userSettings.maView && (
              <div className="mt-8 flex gap-2 flex-wrap">
                <CopyAllButton data={editedData} userSettings={userSettings} customSections={customSections} />
                <button
                  onClick={() => setShowAddSectionModal(true)}
                  className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 rounded-md hover:bg-gray-50 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  <span>Custom Section</span>
                </button>
                {(originalTranscription || shouldShowRecordingDuration) && (
                  <div className="flex items-center gap-2 flex-wrap">
                    {originalTranscription && (
                      <button
                        onClick={() => setShowTranscriptionModal(true)}
                        className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 rounded-md hover:bg-gray-50 transition-colors"
                      >
                        <FileText className="w-4 h-4" />
                        <span>Show Transcription</span>
                      </button>
                    )}
                    {shouldShowRecordingDuration && (
                      <div className="px-4 py-2 rounded-md border border-gray-200 text-sm text-gray-600 bg-white">
                        <span className="font-medium text-gray-700">Recording Time:</span>{' '}
                        {formatRecordingDuration(displayedRecordingDuration)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        </div>
        
        {!isEditingInfo && (
          <div className="text-sm text-gray-600">
            {/* Only show information if it exists */}
            {patient.legal_sex && <p>Sex: {patient.legal_sex}</p>}
            {patient.birth_sex && (
              <p>Birth Sex: {patient.birth_sex}</p>
            )}
            {patient.email && <p>Email: {patient.email}</p>}
            {patient.phone && <p>Phone: {patient.phone}</p>}
            {(patient.visit_type || patient.selected_reason) && (
              <p>Reason for visit: {getVisitTypeDisplayName(patient.visit_type || patient.selected_reason)}</p>
            )}

            {/* Scheduling Status */}
            {/*patient.isScheduled && (
              <p>
                <span className="inline-flex items-center mr-1">
                  <span className="w-2 h-2 rounded-full bg-indigo-500 mr-1"></span>
                  <span className="font-medium">Scheduled</span>
                </span>
                {patient.questionnaire_completed_at && (
                  <span className="text-xs ml-1 text-green-600">(Questionnaire Completed)</span>
                )}
              </p>
            )*/}

            {/* Appointment Time - show if available */}
            {/*patient.appointment_time && patient.appointment_date && (
              <p className="flex items-center">
                <svg 
                  className="w-3 h-3 mr-1 text-indigo-500" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24" 
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
                Appointment: {patient.appointment_time} {patient.appointment_date}
              </p>
            )*/}
            
            {/* Primary Insurance - only show if has_insurance exists and is "True" */}
            {patient.has_insurance === 'True' && <p>Primary Insurance: Yes</p>}
            
            {/* Insurance Cards - only show if they exist */}
            {patient?.InsuranceCards && (
              <p>
                <a
                  href={patient.InsuranceCards}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-teal-600 hover:text-teal-700"
                >
                  View Primary Insurance Images
                </a>
              </p>
            )}
            
            {/* Secondary Insurance - only show if has_secondary_insurance exists and is "True" */}
            {patient.has_secondary_insurance === 'True' && <p>Secondary Insurance: Yes</p>}
          </div>
        )}
      </div>
      
      {/* Right side - Show Transcription button */}
      {/*<div className="flex items-start">
        {originalTranscription && (
          <button
            onClick={() => setShowTranscriptionModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <Globe className="w-4 h-4" />
            Show Transcription
          </button>
        )}
      </div>*/}

    {patient?.is_combined ? (
      <div className="soap-notes-container space-y-8 transition-opacity duration-300 ease-in-out animate-fade-in">
        <button
          onClick={handleCopyAllCombinedNotes}
          className="w-full mb-4 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md flex items-center justify-center"
        >
          <ClipboardCopyIcon className="w-4 h-4 mr-2" />
          Copy All Patient Notes
        </button>
        
        {/* Blaine's Prompts toggle - only show for speer and brockton locations */}
        {(currentLocation?.toLowerCase() === 'speer' || currentLocation?.toLowerCase() === 'brockton') && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-sm font-medium text-blue-800">Blaine's Prompts</span>
              </div>
              <button
                onClick={() => handleBlainesPromptsToggle(!useBlainesPrompts)}
                disabled={isSavingBlainesPrompts}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  useBlainesPrompts ? 'bg-blue-600' : 'bg-gray-200'
                } ${isSavingBlainesPrompts ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    useBlainesPrompts ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {loadedPatientData.map((individualPatient, index) => (
            <div key={individualPatient.id} 
              className="border border-gray-200 rounded-lg p-4 mb-6">
              <div className="mb-4 pb-2 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-lg font-medium text-teal-600">
                  {formatPatientName(individualPatient) || `Patient ${index + 1}`}
                </h3>

                {!userSettings.maView && (
                  <button
                    onClick={() => handleCopyIndividualPatientNotes(individualPatient)}
                    className="px-2 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md flex items-center text-sm"
                  >
                    <ClipboardCopyIcon className="w-4 h-4 mr-1" />
                    Copy Notes
                  </button>
                )}
                <div className="flex flex-col">
                  <p className="text-sm text-gray-500">
                    {individualPatient.dob && (individualPatient.dob.includes('/') || individualPatient.dob.includes('-'))
                      ? `DOB: ${individualPatient.dob}`
                      : individualPatient.dob
                        ? `Age: ${individualPatient.dob}${individualPatient.dob.toString().includes('Y') ? '' : ' Y'}`
                        : ''}
                  </p>

                  {!userSettings.maView && (
                    <div className="mt-2 w-full max-w-xs">
                      <PronounSelector
                        selectedPronouns={individualPatient.pronouns || ''}
                        onPronounsChange={(newPronouns) => handleCombinedPronounsChange(individualPatient.id, newPronouns)}
                        isDisabled={isUpdatingPronouns}
                      />
                    </div>
                  )}
                </div>
              </div>
              
              <div className="space-y-3">
                {userSettings.showEMCodes &&
                  <EMCodesSection
                    codes={individualPatient.em_codes || []}
                    userSettings={userSettings}
                    onInfoClick={() => scrollToEMEducationForPatient(individualPatient.id)}
                  />}

                {userSettings.suggestPOCTests &&
                  <SuggestedTestsSection tests={individualPatient.suggested_tests || []} userSettings={userSettings} />}

                {userSettings.showICDCodes &&
                  <ICDCodesSection codes={individualPatient.icd_codes || []} userSettings={userSettings} />}

                {userSettings.showCPTCodes &&
                  <CPTCodesSection codes={individualPatient.cpt_codes || []} userSettings={userSettings} currentLocation={currentLocation} patientLocation={individualPatient.location} />}

                {userSettings.showVisitSummary && (
                  <EditableSection
                    title="Visit Summary"
                    items={
                      typeof individualPatient.visit_summary === 'string' && individualPatient.visit_summary.trim() !== ''
                        ? [individualPatient.visit_summary]
                        : Array.isArray(individualPatient.visit_summary) && individualPatient.visit_summary.length > 0
                          ? (userSettings.visitSummaryFormat === 'bullet'
                              ? individualPatient.visit_summary
                              : [individualPatient.visit_summary.join(' ')])
                          : ['']
                    }
                    onUpdate={(title, items) => handleCombinedPatientUpdate(individualPatient.id, title, items)}
                    activeSection={activeSection === `${individualPatient.id}-Visit Summary` ? 'Visit Summary' : null}
                    onSectionClick={(title) => setActiveSection(`${individualPatient.id}-${title}`)}
                    isCompact={true}
                    userSettings={userSettings}
                    maView={userSettings.maView}
                  />
                )}

                {/* Conditional rendering based on patient's format */}
                {individualPatient.location === 'siliconbeach' ? (
                  // SOAP Format for this patient
                  <>
                    <EditableSection
                      title="Subjective"
                      items={individualPatient.subjective || []}
                      onUpdate={(title, items) => handleCombinedPatientUpdate(individualPatient.id, title, items)}
                      activeSection={activeSection === `${individualPatient.id}-Subjective` ? 'Subjective' : null}
                      onSectionClick={(title) => setActiveSection(`${individualPatient.id}-${title}`)}
                      maView={userSettings.maView}
                    />
                    <EditableSection
                      title="Objective"
                      items={individualPatient.objective || []}
                      onUpdate={(title, items) => handleCombinedPatientUpdate(individualPatient.id, title, items)}
                      activeSection={activeSection === `${individualPatient.id}-Objective` ? 'Objective' : null}
                      onSectionClick={(title) => setActiveSection(`${individualPatient.id}-${title}`)}
                      maView={userSettings.maView}
                    />
                    <EditableSection
                      title="Assessment"
                      items={individualPatient.assessment || []}
                      onUpdate={(title, items) => handleCombinedPatientUpdate(individualPatient.id, title, items)}
                      activeSection={activeSection === `${individualPatient.id}-Assessment` ? 'Assessment' : null}
                      onSectionClick={(title) => setActiveSection(`${individualPatient.id}-${title}`)}
                      maView={userSettings.maView}
                    />
                    <EditableSection
                      title="Plan"
                      items={individualPatient.plan || []}
                      onUpdate={(title, items) => handleCombinedPatientUpdate(individualPatient.id, title, items)}
                      activeSection={activeSection === `${individualPatient.id}-Plan` ? 'Plan' : null}
                      onSectionClick={(title) => setActiveSection(`${individualPatient.id}-${title}`)}
                      maView={userSettings.maView}
                    />
                  </>
                ) : (
                  // Standard Format for this patient
                  <>
                    <EditableSection
                      title="History of Present Illness"
                      items={individualPatient.hpi || []}
                      onUpdate={(title, items) => handleCombinedPatientUpdate(individualPatient.id, title, items)}
                      activeSection={activeSection === `${individualPatient.id}-History of Present Illness` ? 'History of Present Illness' : null}
                      onSectionClick={(title) => setActiveSection(`${individualPatient.id}-${title}`)}
                      maView={userSettings.maView}
                    />

                    {userSettings.showROS &&
                      <EditableSection
                        title="Review of Symptoms"
                        items={individualPatient.ros || []}
                        onUpdate={(title, items) => handleCombinedPatientUpdate(individualPatient.id, title, items)}
                        activeSection={activeSection === `${individualPatient.id}-Review of Symptoms` ? 'Review of Symptoms' : null}
                        onSectionClick={(title) => setActiveSection(`${individualPatient.id}-${title}`)}
                        maView={userSettings.maView}
                      />
                    }

                    {userSettings.showPhysicalExam &&
                      <EditableSection
                        title="Physical Exam"
                        items={individualPatient.physical_exam || []}
                        onUpdate={(title, items) => handleCombinedPatientUpdate(individualPatient.id, title, items)}
                        activeSection={activeSection === `${individualPatient.id}-Physical Exam` ? 'Physical Exam' : null}
                        onSectionClick={(title) => setActiveSection(`${individualPatient.id}-${title}`)}
                        userSettings={userSettings}
                        isSoapFormat={isSoapFormat}
                        sectionStyle={sectionStyles.physical_exam}
                        onSectionStyleChange={handleSectionStyleChange}
                        maView={userSettings.maView}
                      />
                    }

                    <EditableSection
                      title="Assessment & Plan"
                      items={individualPatient.assessment_plan || []}
                      onUpdate={(title, items) => handleCombinedPatientUpdate(individualPatient.id, title, items)}
                      activeSection={activeSection === `${individualPatient.id}-Assessment & Plan` ? 'Assessment & Plan' : null}
                      onSectionClick={(title) => setActiveSection(`${individualPatient.id}-${title}`)}
                      maView={userSettings.maView}
                    />

                    {userSettings.showAdditionalMDM &&
                      <EditableSection
                        title="Additional MDM"
                        items={individualPatient.additional_mdm || []}
                        onUpdate={(title, items) => handleCombinedPatientUpdate(individualPatient.id, title, items)}
                        activeSection={activeSection === `${individualPatient.id}-Additional MDM` ? 'Additional MDM' : null}
                        onSectionClick={(title) => setActiveSection(`${individualPatient.id}-${title}`)}
                        sectionStyle={sectionStyles.additional_mdm}
                        onSectionStyleChange={handleSectionStyleChange}
                        sendWithValue={customSectionSendWith['additional_mdm'] || 'none'}
                        onSendWithChange={handleSendWithChange}
                        enableSendWithSelector
                        maView={userSettings.maView}
                      />
                    }
                  </>
                )}

                {/* MDM Calculator Section - Only for calimesa location */}
                {individualPatient?.location === 'calimesa' && (
                  <EditableSection
                    title="MDM Calculator"
                    items={individualPatient.mdm_calculator || []}
                    onUpdate={(title, items) => handleCombinedPatientUpdate(individualPatient.id, title, items)}
                    activeSection={activeSection === `${individualPatient.id}-MDM Calculator` ? 'MDM Calculator' : null}
                    onSectionClick={(title) => setActiveSection(`${individualPatient.id}-${title}`)}
                    maView={userSettings.maView}
                  />
                )}

                <EditableSection
                  title="Patient Instructions"
                  items={individualPatient.after_visit_summary || []}
                  onUpdate={(title, items) => handleCombinedPatientUpdate(individualPatient.id, title, items)}
                  activeSection={activeSection === `${individualPatient.id}-Patient Instructions` ? 'Patient Instructions' : null}
                  onSectionClick={(title) => setActiveSection(`${individualPatient.id}-${title}`)}
                  userSettings={userSettings}
                  isSoapFormat={individualPatient.location === 'siliconbeach'}
                  sectionStyle={sectionStyles.after_visit_summary}
                  onSectionStyleChange={(style) => handleSectionStyleChange('after_visit_summary', style)}
                  isCompact={false}
                  maView={userSettings.maView}
                />

                {/* E/M Education Section - at the bottom */}
                <EMEducationSection
                  ref={(el) => (emEducationRefsMap.current[individualPatient.id] = el)}
                  education={individualPatient.em_education || []}
                  userSettings={userSettings}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    ) : (
      // Original single patient display
      !patient.has_soap_note ? (
        <div className="text-center py-8 text-gray-500">
          <p>No SOAP notes available for this patient.</p>
        </div>
      ) : (
        <div className="soap-notes-container space-y-3 transition-opacity duration-300 ease-in-out animate-fade-in">
          
          {/* Blaine's Prompts toggle - only show for speer and brockton locations */}
          {(currentLocation?.toLowerCase() === 'speer' || currentLocation?.toLowerCase() === 'brockton') && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <span className="text-sm font-medium text-blue-800">AFC Prompts</span>
                </div>
                <button
                  onClick={() => handleBlainesPromptsToggle(!useBlainesPrompts)}
                  disabled={isSavingBlainesPrompts}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    useBlainesPrompts ? 'bg-blue-600' : 'bg-gray-200'
                  } ${isSavingBlainesPrompts ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      useBlainesPrompts ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          )}
          
          {/* EM Codes at the top - hidden in All Valley MA View */}
          {!userSettings.allValleyMAView && (
            <EMCodesSection codes={editedData.em_codes} userSettings={userSettings} onInfoClick={scrollToEMEducation} />
          )}

          {/* Common elements for both formats - hidden in All Valley MA View */}
          {!userSettings.allValleyMAView && userSettings.suggestPOCTests &&
            <SuggestedTestsSection tests={editedData.suggested_tests} userSettings={userSettings} />}

          <ICDCodesSection codes={editedData.icd_codes} userSettings={userSettings} />

          {/* MA Section - shown here (after ICD codes) only when allValleyMAView is enabled */}
          {userSettings.allValleyMAView && (
            <MASection
              medications={editedData.ma_medications}
              inhouseLabs={editedData.ma_inhouse_labs}
              sendoutLabs={editedData.ma_sendout_labs}
              procedures={editedData.ma_procedures}
              patientLocation={patient?.location}
              onSave={handleMASectionUpdate}
              isEditable={!userSettings.maView}
            />
          )}

          {/* CPT Codes - hidden in All Valley MA View */}
          {!userSettings.allValleyMAView && (
            <CPTCodesSection codes={editedData.cpt_codes} userSettings={userSettings} currentLocation={currentLocation} patientLocation={patient?.location} />
          )}

          {!userSettings.allValleyMAView && userSettings.showVisitSummary && (
            <EditableSection
              title="Visit Summary"
              items={
                typeof editedData.visit_summary === 'string' && editedData.visit_summary.trim() !== ''
                  ? [editedData.visit_summary]
                  : Array.isArray(editedData.visit_summary) && editedData.visit_summary.length > 0
                    ? (userSettings.visitSummaryFormat === 'bullet'
                        ? editedData.visit_summary
                        : [editedData.visit_summary.join(' ')])
                    : ['']
              }
              onUpdate={handleUpdate}
              activeSection={activeSection}
              onSectionClick={setActiveSection}
              isCompact={true}
              userSettings={userSettings}
              isSoapFormat={isSoapFormat}
              maView={userSettings.maView}
            />
          )}

          {/* Conditional rendering based on format - hidden in All Valley MA View */}
          {!userSettings.allValleyMAView && (isSoapFormat ? (
            // SOAP Format (Silicon Beach)
            <>
              <EditableSection
                title="Subjective"
                items={editedData.subjective}
                onUpdate={handleUpdate}
                activeSection={activeSection}
                onSectionClick={setActiveSection}
                isSoapFormat={isSoapFormat}
                maView={userSettings.maView}
              />
              <EditableSection
                title="Objective"
                items={editedData.objective}
                onUpdate={handleUpdate}
                activeSection={activeSection}
                onSectionClick={setActiveSection}
                isSoapFormat={isSoapFormat}
                maView={userSettings.maView}
              />
              <EditableSection
                title="Assessment"
                items={editedData.assessment}
                onUpdate={handleUpdate}
                activeSection={activeSection}
                onSectionClick={setActiveSection}
                isSoapFormat={isSoapFormat}
                maView={userSettings.maView}
              />
              <EditableSection
                title="Plan"
                items={editedData.plan}
                onUpdate={handleUpdate}
                activeSection={activeSection}
                onSectionClick={setActiveSection}
                isSoapFormat={isSoapFormat}
                maView={userSettings.maView}
              />
            </>
          ) : (
            // Standard Format (Evergreen/Default)
            <>
              <EditableSection
                title="History of Present Illness"
                items={editedData.hpi}
                onUpdate={handleUpdate}
                activeSection={activeSection}
                onSectionClick={setActiveSection}
                userSettings={userSettings}
                isSoapFormat={isSoapFormat}
                sectionStyle={sectionStyles.hpi}
                onSectionStyleChange={handleSectionStyleChange}
                maView={userSettings.maView}
              />
              {userSettings.showROS &&
                <EditableSection
                  title="Review of Symptoms"
                  items={editedData.ros}
                  onUpdate={handleUpdate}
                  activeSection={activeSection}
                  onSectionClick={setActiveSection}
                  userSettings={userSettings}
                  isSoapFormat={isSoapFormat}
                  sectionStyle={sectionStyles.ros}
                  onSectionStyleChange={handleSectionStyleChange}
                  maView={userSettings.maView}
                />
              }
              {userSettings.showPhysicalExam &&
                <EditableSection
                  title="Physical Exam"
                  items={editedData.physical_exam}
                  onUpdate={handleUpdate}
                  activeSection={activeSection}
                  onSectionClick={setActiveSection}
                  userSettings={userSettings}
                  isSoapFormat={isSoapFormat}
                  sectionStyle={sectionStyles.physical_exam}
                  onSectionStyleChange={handleSectionStyleChange}
                  maView={userSettings.maView}
                />
              }
              <EditableSection
                title="Assessment & Plan"
                items={editedData.assessment_plan}
                onUpdate={handleUpdate}
                activeSection={activeSection}
                onSectionClick={setActiveSection}
                userSettings={userSettings}
                isSoapFormat={isSoapFormat}
                sectionStyle={sectionStyles.assessment_plan}
                onSectionStyleChange={handleSectionStyleChange}
                maView={userSettings.maView}
              />

              {userSettings.showAdditionalMDM &&
                <EditableSection
                  title="Additional MDM"
                  items={editedData.additional_mdm || []}
                  onUpdate={handleUpdate}
                  activeSection={activeSection}
                  onSectionClick={setActiveSection}
                  userSettings={userSettings}
                  isSoapFormat={isSoapFormat}
                  sectionStyle={sectionStyles.additional_mdm}
                  onSectionStyleChange={handleSectionStyleChange}
                  sendWithValue={customSectionSendWith['additional_mdm'] || 'none'}
                  onSendWithChange={handleSendWithChange}
                  enableSendWithSelector
                  maView={userSettings.maView}
                />
              }
            </>
          ))}

          {/* MDM Calculator Section - Only for calimesa location - hidden in All Valley MA View */}
          {!userSettings.allValleyMAView && patient?.location === 'calimesa' && (
            <EditableSection
              title="MDM Calculator"
              items={editedData.mdm_calculator || []}
              onUpdate={handleUpdate}
              activeSection={activeSection}
              onSectionClick={setActiveSection}
              userSettings={userSettings}
              isSoapFormat={isSoapFormat}
              maView={userSettings.maView}
            />
          )}

          {/* Patient Instructions - Common to both formats - hidden in All Valley MA View */}
          {!userSettings.allValleyMAView && (
            <EditableSection
              title="Patient Instructions"
              items={editedData.after_visit_summary}
              onUpdate={handleUpdate}
              activeSection={activeSection}
              onSectionClick={setActiveSection}
              userSettings={userSettings}
              isSoapFormat={isSoapFormat}
              sectionStyle={sectionStyles.after_visit_summary}
              onSectionStyleChange={(style) => handleSectionStyleChange('after_visit_summary', style)}
              isCompact={false}
              maView={userSettings.maView}
            />
          )}

          {/* Custom Sections - hidden in All Valley MA View */}
          {!userSettings.allValleyMAView && customSections.map(section => (
            <div key={section.id}>
              <EditableSection
                title={section.title}
                items={editedData[`custom_${section.id}`] || ['']}
                onUpdate={(title, newData) => {
                  handleUpdate(title, newData);
                }}
                activeSection={activeSection}
                onSectionClick={setActiveSection}
                userSettings={userSettings}
                isSoapFormat={isSoapFormat}
                sectionStyle={sectionStyles[`custom_${section.id}`] || 'normal'}
                onSectionStyleChange={handleSectionStyleChange}
                customSectionId={section.id}
                onDeleteSection={handleDeleteCustomSection}
                sendWithValue={customSectionSendWith[`custom_${section.id}`] || 'none'}
                onSendWithChange={handleSendWithChange}
                maView={userSettings.maView}
              />
            </div>
          ))}

          {/* E/M Education Section - after custom sections - hidden in All Valley MA View */}
          {!userSettings.allValleyMAView && (
            <EMEducationSection ref={emEducationRef} education={editedData.em_education} userSettings={userSettings} />
          )}

          {/* MA Section - shown at the end when allValleyMAView is OFF (for calimesa only) */}
          {!userSettings.allValleyMAView && (
            <MASection
              medications={editedData.ma_medications}
              inhouseLabs={editedData.ma_inhouse_labs}
              sendoutLabs={editedData.ma_sendout_labs}
              procedures={editedData.ma_procedures}
              patientLocation={patient?.location}
              onSave={handleMASectionUpdate}
              isEditable={!userSettings.maView}
            />
          )}
        </div>
      )
    )}

    <FloatingControls
        isRecording={isRecording}
        isTranscribing={isTranscribing}
        startRecording={startRecording}
        stopRecording={stopRecording}
        handleSendToEHR={handleSendToEHR}
        isSendingToEHR={isSendingToEHR}
        showSentSuccess={showSentSuccess}
        patient={patient}
        detectedLanguage={detectedLanguage}
        selectedLanguage={selectedLanguage}
        ehrErrorStatus={ehrErrorStatus}
        ehrCooldownNotice={ehrCooldownNotice}
        isEhrCooldown={isEhrCooldown}
        maView={userSettings.maView || userSettings.allValleyMAView}
      />
      
    <DeleteConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        patientName={formatPatientName(patient)}
        isDeleting={isDeleting}
      />
      
    {/* Add Section Modal */}
    {showAddSectionModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
          <h3 className="text-lg font-medium mb-4">Add Custom Section</h3>
            
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Section Title
            </label>
            <input
              type="text"
              value={newSectionTitle}
              onChange={(e) => setNewSectionTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              placeholder="e.g., Social History"
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Section Prompt
            </label>
            <textarea
              value={newSectionPrompt}
              onChange={(e) => setNewSectionPrompt(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              placeholder="Describe what information should be included in this section..."
            />
          </div>

          <div className="flex gap-3 justify-end">
            <button
              onClick={() => {
                setShowAddSectionModal(false);
                setNewSectionTitle('');
                setNewSectionPrompt('');
              }}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleAddSection}
              disabled={!newSectionTitle.trim() || !newSectionPrompt.trim()}
              className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    )}
    </div>
  );
};
